import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState, useCallback, useMemo, memo } from "react";
import { calculateFitFontSize } from "../lib/bingoUtils";

// Compute {cx, cy} for a badge (board number / almost-winner star) given a
// position id. Supports legacy 4-value ids ('top','bottom','left','right')
// meaning "center of that side", plus the new 12-value grid where each side
// has 3 sub-slots:
//   Top / Bottom rows  : '<side>-left', '<side>', '<side>-right'
//   Left / Right sides : '<side>-top',  '<side>', '<side>-bottom'
// Edge slots pull inward by ~half a badge so they don't disappear off-corner.
const computeBadgePosition = (pos, bx, by, bw, bh, badgeSize, offsetFactor) => {
  const off = badgeSize * 0.7 * offsetFactor;
  const inset = badgeSize * 0.5; // pull edge slots inward so corners stay readable
  const isTop    = pos === 'top'    || pos === 'top-left'    || pos === 'top-right';
  const isBottom = pos === 'bottom' || pos === 'bottom-left' || pos === 'bottom-right';
  const isLeft   = pos === 'left'   || pos === 'left-top'    || pos === 'left-bottom';
  const isRight  = pos === 'right'  || pos === 'right-top'   || pos === 'right-bottom';
  let cx, cy;
  if (isTop || isBottom) {
    cy = isTop ? by - off : by + bh + off;
    if (pos.endsWith('-left'))       cx = bx + inset;
    else if (pos.endsWith('-right')) cx = bx + bw - inset;
    else                              cx = bx + bw / 2;
  } else if (isLeft || isRight) {
    cx = isLeft ? bx - off : bx + bw + off;
    if (pos.endsWith('-top'))         cy = by + inset;
    else if (pos.endsWith('-bottom')) cy = by + bh - inset;
    else                               cy = by + bh / 2;
  } else {
    // Unknown id — fall back to top-center
    cx = bx + bw / 2;
    cy = by - off;
  }
  return { cx, cy };
};

const CanvasEditor = forwardRef(({
  templateSize,
  backgroundColor,
  backgroundImage,
  backgroundImageFit,
  // User zoom multiplier applied to the computed dimensions (after the fit
  // mode chooses cover/contain/stretch). 1.0 = no extra zoom. Default 1.
  backgroundImageScale,
  overlayImages,
  selectedOverlayId,
  setSelectedOverlayId,
  overlayShadow,
  overlayShadowOffsetX = 2,
  overlayShadowOffsetY = 2,
  updateOverlayPosition,
  updateOverlaySize,
  bingoBoards,
  selectedBoardId,
  setSelectedBoardId,
  updateBoardPosition,
  moveLoteriaColumn,
  moveLoteriaRow,
  moveBingoColumn,
  moveBingoRow,
  loteriaMoveMode,
  updateAllBoardsSize,
  boardStyle,
  boardColor,
  headerColor,
  headerLetterColor,
  cellColor,
  textColor,
  fontFamily,
  headerFontFamily,
  freeSpaceText,
  freeSpaceColor,
  freeSpaceTextColor,
  freeSpaceNumbering,
  freeSpaceTextEffect,
  freeSpaceTextThickness,
  boardOpacity,
  headerOpacity,
  cellOpacity,
  freeSpaceOpacity,
  watermarkEnabled,
  watermarkText,
  watermarkOpacity,
  watermarkColor,
  watermarkFont,
  templateTextEnabled,
  templateText,
  templateTextFont,
  templateTextColor,
  templateTextOutlineEnabled,
  templateTextOutlineColor,
  templateTextOutlineWidth,
  templateTextSize,
  templateTextX,
  templateTextY,
  templateTextLetterSpacing,
  templateTextOpacity,
  extraTemplateTexts,
  onExtraTextMove,
  onTemplateTextMove,
  prizeDots,
  setPrizeDots,
  markupElements,
  activeMarkupTool,
  markupColor,
  markupThickness,
  eraserThickness,
  markupTipStyle,
  markupOpacity,
  onMarkupComplete,
  onEraseMarkup,
  selectedMarkupId,
  setSelectedMarkupId,
  moveMarkupElement,
  resizeMarkupElement,
  markupStampImage,
  markupStampTemplate,
  setMarkupStampTemplate,
  emojis,
  selectedEmojiId,
  setSelectedEmojiId,
  updateEmojiPosition,
  filterSettings,
  saveToHistory,
  isFullscreen,
  normalDisplayWidth,
  normalDisplayHeight,
  // Animation Background (caller bg video) — rendered behind the canvas
  // at exactly displayWidth × displayHeight in both fullscreen and regular
  // mode. Owner-only feature. Canvas should pass `skipBackground` when this
  // is set so the video shows through.
  callerBgVideoSrc,
  callerBgVideoOpacity = 60,
  callerBgVideoFit = 'cover',
  obsMode = false,
  isOwner: bgVideoIsOwner = false,
  transparentBoards,
  transparentKeepHeader,
  transparentKeepFreeSpace,
  multiColorRows,
  boardRowColors,
  highlightedBoards,
  highlightedCells,
  highlightColor,
  highlightBoardNumOnAutoMarkup,
  autoMarkedBoards,
  hammerMode,
  boardEffectType = 'hammer',
  shakingBoards,
  shakeTick: _shakeTick, // forces re-render; intentionally unused below
  onHammerStrike,
  hammerStrike,
  scratchDecals,
  shamrockParticles,
  waterStrikes,
  almostWinnerBoards,
  almostWinnerRows,
  almostWinnerCols,
  loteriaStarBoard,
  loteriaStarRowCol,
  starColor,
  starOpacity,
  starStyle,
  gameMode,
  loteriaBoards,
  loteriaFigures,
  loteriaGridSize,
  loteriaCellColors,
  loteriaColorMode,
  loteriaRowColors,
  loteriaColColors,
  loteriaStripeColors,
  loteriaBorderColor,
  loteriaGridColor,
  loteriaCenterHighlight,
  loteriaCenterHighlightColor,
  loteriaCenterHighlightThickness,
  loteriaCenterHighlightOffsetX,
  loteriaCenterHighlightOffsetY,
  loteriaRepeatMode,
  loteriaRepeatHighlight,
  loteriaRepeatHighlightColor,
  loteriaRepeatHighlightThickness,
  loteriaBorderStyle,
  loteriaCornerRadius,
  loteriaBorderWidth,
  loteriaGridWidth,
  loteriaCellShape,
  loteriaDeck,
  loteriaBoardShadow,
  loteriaNumbering,
  // Per-board numbering overrides (map: boardIdx → { badgeShiftX, badgeShiftY }).
  // Applied per-board so Custom Vertical Pairs can move their own board
  // number badges without affecting any other boards.
  loteriaBoardNumberingOverrides,
  // Optional map: boardIdx → display number override. When provided (Neighbors
  // Vertical Pair Numbering toggle ON), the board's badge renders this number
  // instead of the default (boardIdx + startNum).
  loteriaBoardDisplayNums,
  loteriaFigureImagesRef,
  loteriaImagesLoaded,
  isolatedBoardNums,
  neighborsEnabled,
  neighborsPosition,
  neighborsDirection,
  neighborsHighlightColor,
  neighborsShape,
  neighborsBorderThickness,
  neighborsShadowEnabled,
  neighborsOutlineStyle,
  neighborsCustomImage,
  neighborsCustomImageSize,
  neighborsCustomOutlineWidth,
  neighborsCustomOutlineColor,
  neighborPairs,
  lastCardEnabled,
  lastCardShape,
  lastCardHighlightColor,
  lastCardBorderThickness,
  lastCardShadowEnabled,
  lastCardOutlineStyle,
  lastCardTilt,
  lastCardOffsetX,
  lastCardOffsetY,
  lastCardScale,
  lastCardAssignments,
  canvasDPI,
  skipBackground,
}, ref) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const imageCache = useRef(new Map());
  const figureBgColorCache = useRef(new Map()); // figure id -> sampled card background color
  // Uploaded custom badge background image (works like a stamp behind the
  // board-number). Loaded once per data-URL change; drawBadge reads
  // `badgeImgRef.current` synchronously when shape === 'image'.
  const badgeImgRef = useRef(null);
  const [badgeImgVersion, setBadgeImgVersion] = useState(0);
  useEffect(() => {
    const src = loteriaNumbering && loteriaNumbering.badgeImage;
    if (!src) { badgeImgRef.current = null; setBadgeImgVersion(v => v + 1); return; }
    const img = new Image();
    img.onload = () => {
      badgeImgRef.current = img;
      setBadgeImgVersion(v => v + 1);
    };
    img.onerror = () => { badgeImgRef.current = null; setBadgeImgVersion(v => v + 1); };
    img.src = src;
  }, [loteriaNumbering && loteriaNumbering.badgeImage]);
  // Uploaded custom neighbor / connecting image. When set, ALL neighbor
  // pairs draw this image instead of a Loteria figure card.
  const neighborsCustomImgRef = useRef(null);
  const [neighborsCustomImgVersion, setNeighborsCustomImgVersion] = useState(0);
  useEffect(() => {
    const src = neighborsCustomImage;
    if (!src) { neighborsCustomImgRef.current = null; setNeighborsCustomImgVersion(v => v + 1); return; }
    const img = new Image();
    img.onload = () => {
      neighborsCustomImgRef.current = img;
      setNeighborsCustomImgVersion(v => v + 1);
    };
    img.onerror = () => { neighborsCustomImgRef.current = null; setNeighborsCustomImgVersion(v => v + 1); };
    img.src = src;
  }, [neighborsCustomImage]);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [dragTarget, setDragTarget] = useState(null);
  const [activeDrawing, setActiveDrawing] = useState(null);
  // Pending freehand/shape point queued by mousemove, flushed once per
  // animation frame so high-refresh-rate displays don't trigger 100+
  // React re-renders per second during a single stroke. Without this the
  // entire canvas (boards + overlays + markup) would redraw every move.
  const pendingDrawRef = useRef(null); // { points: [...], endPoint: {x,y} | null }
  const drawRAFRef = useRef(null);
  // Tick counter used to force re-render after async asset loads (e.g.,
  // stamp image finished loading into imageCache).
  const [, setRenderTick] = useState(0);
  // Tracks the start of a drag-to-move gesture on a selected markup element.
  // Set on mousedown (Select tool + element hit), updated each mousemove,
  // cleared on mouseup.
  const markupDragOriginRef = useRef(null);
  const [containerSize, setContainerSize] = useState({ w: 0, h: 0 });
  const [snapGuides, setSnapGuides] = useState([]);  // { type: 'h'|'v', pos: number }

  // Multi-click tracking for Loteria row/column group move
  const clickCountRef = useRef(0);
  const clickTimerRef = useRef(null);
  const loteriaDragModeRef = useRef('single'); // 'single' | 'column' | 'row'
  const [loteriaDragMode, setLoteriaDragMode] = useState('single'); // for UI display
  const didDragRef = useRef(false); // tracks whether actual dragging occurred in this interaction
  const lastDragPosRef = useRef({ x: 0, y: 0 });

  useImperativeHandle(ref, () => canvasRef.current);

  // Track container size for fullscreen
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const parent = containerRef.current.parentElement;
        if (parent) {
          setContainerSize({ w: parent.clientWidth, h: parent.clientHeight });
        }
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [isFullscreen]);

  const DPI = canvasDPI || 150;
  const canvasWidth = templateSize.width * DPI;
  const canvasHeight = templateSize.height * DPI;
  // Supersampling factor for the canvas BACKING store. Coordinates stay in
  // canvasWidth × canvasHeight space (so overlays / boards / markup keep
  // working unchanged); we just double the backing pixels + apply a
  // ctx.setTransform(scale, ...) at draw time. Only activates when the
  // user explicitly turns on OBS Mode, so regular preview + fullscreen
  // rendering remain byte-for-byte identical to the pre-OBS behavior.
  const superSample = obsMode ? 2 : 1;

  const { displayWidth, displayHeight, scale } = useMemo(() => {
    if (isFullscreen && containerSize.w > 0 && containerSize.h > 0) {
      // Fit to viewport: prioritize filling height, maintain aspect ratio
      const ar = canvasWidth / canvasHeight;
      const maxW = containerSize.w;
      const maxH = containerSize.h;
      let dw, dh;
      // Fit to height first, then check if width overflows
      dh = maxH;
      dw = maxH * ar;
      if (dw > maxW) {
        // If too wide, fit to width instead
        dw = maxW;
        dh = maxW / ar;
      }
      return { displayWidth: dw, displayHeight: dh, scale: dw / canvasWidth };
    }
    const maxDW = 550, maxDH = 650;
    const ar = canvasWidth / canvasHeight;
    let dw, dh;
    if (ar > maxDW / maxDH) { dw = maxDW; dh = maxDW / ar; }
    else { dh = maxDH; dw = maxDH * ar; }
    return { displayWidth: dw, displayHeight: dh, scale: dw / canvasWidth };
  }, [canvasWidth, canvasHeight, isFullscreen, containerSize]);

  // Scale factor from normal display to current display (for fullscreen board scaling)
  const fsScale = useMemo(() => {
    if (!isFullscreen || !normalDisplayWidth || !normalDisplayHeight) return 1;
    return displayWidth / normalDisplayWidth;
  }, [isFullscreen, displayWidth, normalDisplayWidth, normalDisplayHeight]);

  // Preload the stamp image into imageCache so the canvas can draw it
  // immediately on the next render. Re-runs whenever the user uploads a
  // new stamp image. Safely no-ops when image is null (after Remove).
  useEffect(() => {
    if (!markupStampImage || typeof markupStampImage !== 'string') return;
    if (imageCache.current.has(markupStampImage)) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imageCache.current.set(markupStampImage, img);
      // Force a re-render so any already-placed stamps re-draw with the image.
      setRenderTick(t => t + 1);
    };
    img.onerror = () => {
      // Silently ignore — placeholder rect remains visible on the canvas.
    };
    img.src = markupStampImage;
  }, [markupStampImage]);

  // Cached image loader (proxies external URLs to avoid CORS canvas taint)
  const loadCachedImage = useCallback(async (src) => {
    if (imageCache.current.has(src)) {
      // LRU touch — move to end so recently-used images survive eviction.
      const img = imageCache.current.get(src);
      imageCache.current.delete(src);
      imageCache.current.set(src, img);
      return img;
    }
    const img = new Image();
    img.crossOrigin = "anonymous";
    // Proxy external URLs through backend to preserve canvas exportability
    let loadSrc = src;
    if (src && !src.startsWith('data:') && !src.startsWith('/api/') && !src.startsWith(window.location.origin)) {
      loadSrc = `/api/proxy-image?url=${encodeURIComponent(src)}`;
    }
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = loadSrc;
    });
    imageCache.current.set(src, img);
    // Cap cache size to prevent unbounded memory growth on long sessions —
    // critical for iOS Safari which aggressively kills tabs under memory
    // pressure. Cap of 150 images keeps ~all figure packs + overlays hot
    // while giving Safari room to breathe.
    const MAX_CACHE = 150;
    while (imageCache.current.size > MAX_CACHE) {
      const oldestKey = imageCache.current.keys().next().value;
      if (oldestKey === undefined) break;
      imageCache.current.delete(oldestKey);
    }
    return img;
  }, []);

  // Draw a colored silhouette outline around an overlay image's
  // non-transparent pixels. Renders the image's alpha mask in the outline
  // color, then stamps it at 16 offsets around the original position so the
  // colored mask "bleeds out" by `thickness` pixels, producing a uniform
  // outline. Caller draws the real image AFTER this so the outline only
  // shows around the edges (the image covers the inner stamps).
  const drawOverlayOutline = useCallback((ctx, img, overlay, ox, oy, ow, oh, fsScale) => {
    if (!overlay || !overlay.outlineEnabled) return;
    const rawT = overlay.outlineThickness != null ? overlay.outlineThickness : 6;
    // Scale thickness with fullscreen so the outline reads the same visually
    // at SD / HD / 4K. Minimum 1px.
    const t = Math.max(1, Math.round(rawT * (fsScale || 1)));
    const color = overlay.outlineColor || '#000000';

    // Build a colored silhouette of the image at the target render size into
    // an offscreen canvas. We pad by `t` so the bleed has room.
    const padded = document.createElement('canvas');
    padded.width = Math.max(1, Math.round(ow + t * 2));
    padded.height = Math.max(1, Math.round(oh + t * 2));
    const pctx = padded.getContext('2d');
    if (!pctx) return;
    pctx.drawImage(img, t, t, ow, oh);
    // Color the silhouette using source-in (keeps existing alpha, replaces
    // RGB with the outline color).
    pctx.globalCompositeOperation = 'source-in';
    pctx.fillStyle = color;
    pctx.fillRect(0, 0, padded.width, padded.height);

    // Stamp the colored silhouette around the original position to create
    // a uniform bleed. 16 angular samples gives a smooth outline without
    // visible facets.
    const STEPS = 16;
    const saved = { shadowColor: ctx.shadowColor, shadowBlur: ctx.shadowBlur, shadowOffsetX: ctx.shadowOffsetX, shadowOffsetY: ctx.shadowOffsetY };
    // Disable shadow during outline stamping; we only want shadow on the
    // final image draw.
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    for (let i = 0; i < STEPS; i++) {
      const a = (i / STEPS) * Math.PI * 2;
      const dx = Math.cos(a) * t;
      const dy = Math.sin(a) * t;
      ctx.drawImage(padded, ox - t + dx, oy - t + dy);
    }
    // Restore shadow settings for subsequent draws.
    ctx.shadowColor = saved.shadowColor;
    ctx.shadowBlur = saved.shadowBlur;
    ctx.shadowOffsetX = saved.shadowOffsetX;
    ctx.shadowOffsetY = saved.shadowOffsetY;
  }, []);

  // Build CSS filter string
  const buildFilterString = useCallback((fs) => {
    if (!fs) return 'none';
    const parts = [];
    if (fs.brightness !== 100) parts.push(`brightness(${fs.brightness}%)`);
    if (fs.contrast !== 100) parts.push(`contrast(${fs.contrast}%)`);
    if (fs.saturate !== 100) parts.push(`saturate(${fs.saturate}%)`);
    if (fs.grayscale > 0) parts.push(`grayscale(${fs.grayscale}%)`);
    if (fs.sepia > 0) parts.push(`sepia(${fs.sepia}%)`);
    if (fs.blur > 0) parts.push(`blur(${fs.blur}px)`);
    if (fs.hueRotate !== 0) parts.push(`hue-rotate(${fs.hueRotate}deg)`);
    return parts.length > 0 ? parts.join(' ') : 'none';
  }, []);

  // Snap guide threshold (in display coords)
  const SNAP_THRESHOLD = 6;

  // Compute snap guides for a dragged board against other boards
  const computeSnapGuides = useCallback((draggedId, newX, newY, boardType) => {
    const boards = boardType === 'loteria' ? (loteriaBoards || []) : bingoBoards;
    const dragged = boards.find(b => b.id === draggedId);
    if (!dragged) return { guides: [], snappedX: newX, snappedY: newY };

    const dw = dragged.width;
    const dh = boardType === 'loteria' ? dw * LOTERIA_CELL_RATIO : (dw / 5) * 6; // loteria cells are taller

    // Edges + center of the dragged board
    const dLeft = newX, dRight = newX + dw, dCenterX = newX + dw / 2;
    const dTop = newY, dBottom = newY + dh, dCenterY = newY + dh / 2;

    const guides = [];
    let snappedX = newX, snappedY = newY;
    let bestDx = SNAP_THRESHOLD + 1, bestDy = SNAP_THRESHOLD + 1;

    for (const other of boards) {
      if (other.id === draggedId) continue;
      const ow = other.width;
      const oh = boardType === 'loteria' ? ow * LOTERIA_CELL_RATIO : (ow / 5) * 6;
      const oLeft = other.x, oRight = other.x + ow, oCenterX = other.x + ow / 2;
      const oTop = other.y, oBottom = other.y + oh, oCenterY = other.y + oh / 2;

      // Vertical alignment checks (x-axis snaps)
      const vChecks = [
        { d: dLeft, o: oLeft },
        { d: dRight, o: oRight },
        { d: dLeft, o: oRight },
        { d: dRight, o: oLeft },
        { d: dCenterX, o: oCenterX },
      ];
      for (const vc of vChecks) {
        const dist = Math.abs(vc.d - vc.o);
        if (dist < SNAP_THRESHOLD && dist < bestDx) {
          bestDx = dist;
          snappedX = newX + (vc.o - vc.d);
          guides.push({ type: 'v', pos: vc.o });
        }
      }

      // Horizontal alignment checks (y-axis snaps)
      const hChecks = [
        { d: dTop, o: oTop },
        { d: dBottom, o: oBottom },
        { d: dTop, o: oBottom },
        { d: dBottom, o: oTop },
        { d: dCenterY, o: oCenterY },
      ];
      for (const hc of hChecks) {
        const dist = Math.abs(hc.d - hc.o);
        if (dist < SNAP_THRESHOLD && dist < bestDy) {
          bestDy = dist;
          snappedY = newY + (hc.o - hc.d);
          guides.push({ type: 'h', pos: hc.o });
        }
      }
    }

    // Canvas edge snap guides
    const canvasEdges = {
      left: 0, right: displayWidth || 850, top: 0, bottom: displayHeight || 1100,
      centerX: (displayWidth || 850) / 2, centerY: (displayHeight || 1100) / 2,
    };
    const edgeVChecks = [
      { d: dLeft, o: canvasEdges.left },
      { d: dRight, o: canvasEdges.right },
      { d: dCenterX, o: canvasEdges.centerX },
    ];
    for (const vc of edgeVChecks) {
      const dist = Math.abs(vc.d - vc.o);
      if (dist < SNAP_THRESHOLD && dist < bestDx) {
        bestDx = dist;
        snappedX = newX + (vc.o - vc.d);
        guides.push({ type: 'v', pos: vc.o });
      }
    }
    const edgeHChecks = [
      { d: dTop, o: canvasEdges.top },
      { d: dBottom, o: canvasEdges.bottom },
      { d: dCenterY, o: canvasEdges.centerY },
    ];
    for (const hc of edgeHChecks) {
      const dist = Math.abs(hc.d - hc.o);
      if (dist < SNAP_THRESHOLD && dist < bestDy) {
        bestDy = dist;
        snappedY = newY + (hc.o - hc.d);
        guides.push({ type: 'h', pos: hc.o });
      }
    }

    return { guides, snappedX, snappedY };
  }, [bingoBoards, loteriaBoards, displayWidth, displayHeight]);

  // Helper: draw text with effects (outline, neon, shadow, glow)
  const drawTextWithEffect = (ctx, text, x, y, effect, thickness, fillColor, outlineColor) => {
    if (!effect || effect === 'none') {
      ctx.fillText(text, x, y);
      return;
    }
    ctx.save();
    const lw = thickness || 2;
    // The font outline color falls back to the fill color (legacy behaviour
    // — keeps unchanged for text effects that previously used fillColor as
    // the stroke). Pass an explicit outlineColor to override.
    const strokeCol = outlineColor || fillColor;
    if (effect === 'outline') {
      ctx.strokeStyle = strokeCol;
      ctx.lineWidth = lw;
      ctx.lineJoin = 'round';
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
    } else if (effect === 'white-outline') {
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineWidth = lw;
      ctx.lineJoin = 'round';
      ctx.strokeText(text, x, y);
      ctx.fillStyle = fillColor;
      ctx.fillText(text, x, y);
    } else if (effect === 'neon') {
      ctx.shadowColor = strokeCol;
      ctx.shadowBlur = lw * 4;
      ctx.strokeStyle = strokeCol;
      ctx.lineWidth = lw;
      ctx.lineJoin = 'round';
      ctx.strokeText(text, x, y);
      ctx.shadowBlur = lw * 8;
      ctx.fillText(text, x, y);
    } else if (effect === 'shadow') {
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = lw * 2;
      ctx.shadowOffsetX = lw;
      ctx.shadowOffsetY = lw;
      ctx.fillText(text, x, y);
    } else if (effect === 'glow') {
      ctx.shadowColor = strokeCol;
      ctx.shadowBlur = lw * 6;
      ctx.fillText(text, x, y);
      ctx.shadowBlur = lw * 12;
      ctx.fillText(text, x, y);
    }
    ctx.restore();
  };

  // Draw a single Loteria board
  const LOTERIA_CELL_RATIO = (loteriaCellShape === 'square' || loteriaDeck === 'square') ? 1.0 : 1.25;
  const drawLoteriaBoard = useCallback((ctx, board, bx, by, bw, gridSize, cellColors, borderCol, numbering, figureImages, figures, boardIndex, numbersOnly = false) => {
    // Support non-square grids
    const gCols = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[0]) : gridSize;
    const gRows = typeof gridSize === 'string' && gridSize.includes('x') ? parseInt(gridSize.split('x')[1]) : gridSize;
    const cw = bw / gCols;
    const ch = cw * LOTERIA_CELL_RATIO;
    const bh = ch * gRows;
    const baseBorderW = cw * 0.03;
    const borderW = baseBorderW * (loteriaBorderWidth || 100) / 100;
    const gridW = baseBorderW * (loteriaGridWidth || 100) / 100;

    // Multi-Color Rows/Columns: override the board's outer border + inner grid
    // line color based on the board's TEMPLATE-grid position (board.gridRow /
    // board.gridCol). This way every board sharing a template row (or column)
    // is outlined in the same color so the template visually groups them.
    const mode = loteriaColorMode || 'checkerboard';
    const tRow = (board && typeof board.gridRow === 'number') ? board.gridRow : 0;
    const tCol = (board && typeof board.gridCol === 'number') ? board.gridCol : 0;
    let multiColor = null;
    if (mode === 'rows' && loteriaRowColors && loteriaRowColors.length > 0) {
      multiColor = loteriaRowColors[tRow % loteriaRowColors.length];
    } else if (mode === 'columns' && loteriaColColors && loteriaColColors.length > 0) {
      multiColor = loteriaColColors[tCol % loteriaColColors.length];
    } else if (mode === 'rowStripes' && loteriaStripeColors && loteriaStripeColors.length > 0) {
      multiColor = loteriaStripeColors[tRow % loteriaStripeColors.length];
    } else if (mode === 'colStripes' && loteriaStripeColors && loteriaStripeColors.length > 0) {
      multiColor = loteriaStripeColors[tCol % loteriaStripeColors.length];
    }
    const effectiveBorderCol = multiColor || borderCol;
    const gridCol = multiColor || loteriaGridColor || borderCol;

    const style = loteriaBorderStyle || 'sharp';
    // If user has explicitly set a corner-radius % (0-40), it overrides the
    // legacy style-based radius. Otherwise fall back to: soft=8%, pill=20%,
    // anything else=0.
    const explicitRadiusPct = (typeof loteriaCornerRadius === 'number' && loteriaCornerRadius >= 0)
      ? loteriaCornerRadius : null;
    const radius = explicitRadiusPct != null
      ? cw * (explicitRadiusPct / 100)
      : (style === 'soft' ? cw * 0.08 : style === 'pill' ? cw * 0.2 : 0);

    if (!numbersOnly) {
    ctx.save();

    // Board drop shadow (separate from border style shadow)
    const shadow = loteriaBoardShadow || 'none';
    if (shadow !== 'none') {
      const shadowCfg = shadow === 'light'
        ? { color: 'rgba(0,0,0,0.25)', blur: cw * 0.3, ox: cw * 0.06, oy: cw * 0.06 }
        : shadow === 'medium'
        ? { color: 'rgba(0,0,0,0.45)', blur: cw * 0.5, ox: cw * 0.1, oy: cw * 0.1 }
        : { color: 'rgba(0,0,0,0.65)', blur: cw * 0.8, ox: cw * 0.15, oy: cw * 0.15 };
      ctx.shadowColor = shadowCfg.color;
      ctx.shadowBlur = shadowCfg.blur;
      ctx.shadowOffsetX = shadowCfg.ox;
      ctx.shadowOffsetY = shadowCfg.oy;
      // Draw a solid rect to cast the shadow before clipping
      ctx.fillStyle = effectiveBorderCol;
      if (radius > 0) {
        ctx.beginPath();
        ctx.moveTo(bx + radius, by);
        ctx.arcTo(bx + bw, by, bx + bw, by + bh, radius);
        ctx.arcTo(bx + bw, by + bh, bx, by + bh, radius);
        ctx.arcTo(bx, by + bh, bx, by, radius);
        ctx.arcTo(bx, by, bx + bw, by, radius);
        ctx.closePath();
        ctx.fill();
      } else {
        ctx.fillRect(bx, by, bw, bh);
      }
      // Reset shadow after casting
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;
    }

    // Board outer shape (clipping for rounded styles)
    if (radius > 0) {
      ctx.beginPath();
      ctx.moveTo(bx + radius, by);
      ctx.arcTo(bx + bw, by, bx + bw, by + bh, radius);
      ctx.arcTo(bx + bw, by + bh, bx, by + bh, radius);
      ctx.arcTo(bx, by + bh, bx, by, radius);
      ctx.arcTo(bx, by, bx + bw, by, radius);
      ctx.closePath();
      ctx.clip();
    }

    // Shadow for 'shadowed' border style
    if (style === 'shadowed') {
      ctx.shadowColor = 'rgba(0,0,0,0.4)';
      ctx.shadowBlur = cw * 0.15;
      ctx.shadowOffsetX = cw * 0.05;
      ctx.shadowOffsetY = cw * 0.05;
    }

    // Board background (grid color shows through cell gaps as grid lines)
    ctx.fillStyle = gridCol;
    ctx.fillRect(bx, by, bw, bh);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // High quality image rendering
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Compute mode + per-board template-level color ONCE (applied uniformly to
    // every cell of this board). The board's position on the template grid
    // (gridRow / gridCol) — not the cell within the board — drives the color.
    // Checkerboard remains the legacy per-cell mode for backward compatibility.

    // Draw cells with figures
    for (let row = 0; row < gRows; row++) {
      for (let col = 0; col < gCols; col++) {
        const idx = row * gCols + col;
        const cx = bx + col * cw;
        const cy = by + row * ch;

        // Cell background — legacy 2-color checkerboard alternation. Multi-color
        // template modes (rows/columns/stripes) drive the BORDER + GRID lines
        // instead, leaving cell backgrounds untouched.
        const colorIdx = (row + col) % cellColors.length;
        ctx.fillStyle = cellColors[colorIdx] || '#FFFFFF';
        ctx.fillRect(cx + gridW, cy + gridW, cw - gridW * 2, ch - gridW * 2);

        // Draw figure image
        const figureIdx = board.figures[idx];
        if (figureIdx != null && figures[figureIdx]) {
          const fig = figures[figureIdx];
          const imgEl = figureImages?.current?.[fig.id];
          if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
            const pad = cw * 0.02;
            const destX = cx + pad;
            const destY = cy + pad;
            const destW = cw - pad * 2;
            const destH = ch - pad * 2;
            
            if (LOTERIA_CELL_RATIO === 1.0 && imgEl.naturalHeight > imgEl.naturalWidth) {
              const srcW = imgEl.naturalWidth;
              const srcH = imgEl.naturalHeight;
              const cropH = srcW;
              const cropY = (srcH - cropH) / 2;
              ctx.drawImage(imgEl, 0, cropY, srcW, cropH, destX, destY, destW, destH);
            } else {
              ctx.drawImage(imgEl, destX, destY, destW, destH);
            }
          }
        }

        // Cell grid lines (uses separate grid color)
        ctx.strokeStyle = gridCol;
        ctx.lineWidth = gridW;
        if (style === 'dotted') {
          ctx.setLineDash([gridW * 3, gridW * 2]);
        } else if (style === 'double') {
          ctx.setLineDash([]);
          ctx.strokeRect(cx, cy, cw, ch);
          ctx.strokeRect(cx + gridW * 1.5, cy + gridW * 1.5, cw - gridW * 3, ch - gridW * 3);
        } else {
          ctx.setLineDash([]);
        }
        if (style !== 'double') {
          ctx.strokeRect(cx, cy, cw, ch);
        }
      }
    }
    ctx.setLineDash([]);

    // Outer board border (uses borderW, independent from grid lines)
    ctx.strokeStyle = effectiveBorderCol;
    ctx.lineWidth = borderW * 2;
    if (style === 'dotted') ctx.setLineDash([borderW * 4, borderW * 3]);
    if (radius > 0) {
      ctx.beginPath();
      ctx.moveTo(bx + radius, by);
      ctx.arcTo(bx + bw, by, bx + bw, by + bh, radius);
      ctx.arcTo(bx + bw, by + bh, bx, by + bh, radius);
      ctx.arcTo(bx, by + bh, bx, by, radius);
      ctx.arcTo(bx, by, bx + bw, by, radius);
      ctx.closePath();
      ctx.stroke();
    } else {
      ctx.strokeRect(bx, by, bw, bh);
    }
    if (style === 'double') {
      ctx.lineWidth = borderW;
      if (radius > 0) {
        const inset = borderW * 3;
        ctx.beginPath();
        ctx.moveTo(bx + radius + inset, by + inset);
        ctx.arcTo(bx + bw - inset, by + inset, bx + bw - inset, by + bh - inset, Math.max(0, radius - inset));
        ctx.arcTo(bx + bw - inset, by + bh - inset, bx + inset, by + bh - inset, Math.max(0, radius - inset));
        ctx.arcTo(bx + inset, by + bh - inset, bx + inset, by + inset, Math.max(0, radius - inset));
        ctx.arcTo(bx + inset, by + inset, bx + bw - inset, by + inset, Math.max(0, radius - inset));
        ctx.closePath();
        ctx.stroke();
      } else {
        const inset = borderW * 3;
        ctx.strokeRect(bx + inset, by + inset, bw - inset * 2, bh - inset * 2);
      }
    }
    ctx.setLineDash([]);

    // Decorative center highlight ring (Loteria only, purely visual). For a
    // 4x4 grid this rings the center 2x2 block; for 5x5 the center 3x3 block.
    // Border-only (no fill) so the figures stay fully visible. Only square
    // 4x4 / 5x5 grids are supported (the UI hides it for other sizes).
    if (loteriaCenterHighlight && gCols === gRows && (gCols === 4 || gCols === 5)) {
      const span = gCols === 4 ? 2 : 3;
      const s0 = Math.floor((gCols - span) / 2); // centered default (1 for 4x4/5x5)
      const offX = Math.round(loteriaCenterHighlightOffsetX || 0);
      const offY = Math.round(loteriaCenterHighlightOffsetY || 0);
      const clampStart = (v) => Math.max(0, Math.min(gCols - span, v));
      const startCol = clampStart(s0 + offX);
      const startRow = clampStart(s0 + offY);
      const rx = bx + startCol * cw;
      const ry = by + startRow * ch;
      const rw = span * cw;
      const rh = span * ch;
      ctx.save();
      ctx.setLineDash([]);
      ctx.strokeStyle = loteriaCenterHighlightColor || '#FF1493';
      ctx.lineWidth = Math.max(2, cw * 0.08 * ((loteriaCenterHighlightThickness || 100) / 100));
      ctx.lineJoin = 'miter';
      const inset = ctx.lineWidth / 2; // keep the full ring inside the block edges
      ctx.strokeRect(rx + inset, ry + inset, rw - inset * 2, rh - inset * 2);
      ctx.restore();
    }

    // Doubles/Triples highlight — rings every run of 2+ identical adjacent
    // figures: horizontal across a row AND vertical down a column. Border-only
    // (no fill). Shown whenever a repeat mode is active (Loteria only).
    if (loteriaRepeatHighlight && loteriaRepeatMode && loteriaRepeatMode !== 'none' && board && Array.isArray(board.figures)) {
      const figAt = (r, c) => board.figures[r * gCols + c];
      ctx.save();
      ctx.setLineDash([]);
      ctx.strokeStyle = loteriaRepeatHighlightColor || '#00E5FF';
      ctx.lineWidth = Math.max(2, cw * 0.07 * ((loteriaRepeatHighlightThickness || 100) / 100));
      ctx.lineJoin = 'miter';
      const rInset = ctx.lineWidth / 2;
      const ringRun = (rx, ry, rw, rh) => {
        ctx.strokeRect(rx + rInset, ry + rInset, rw - rInset * 2, rh - rInset * 2);
      };
      // Horizontal runs
      for (let row = 0; row < gRows; row++) {
        let c = 0;
        while (c < gCols) {
          const f = figAt(row, c);
          let end = c;
          while (end + 1 < gCols && f != null && figAt(row, end + 1) === f) end++;
          const len = end - c + 1;
          if (f != null && len >= 2) ringRun(bx + c * cw, by + row * ch, len * cw, ch);
          c = end + 1;
        }
      }
      // Vertical runs
      for (let col = 0; col < gCols; col++) {
        let r = 0;
        while (r < gRows) {
          const f = figAt(r, col);
          let end = r;
          while (end + 1 < gRows && f != null && figAt(end + 1, col) === f) end++;
          const len = end - r + 1;
          if (f != null && len >= 2) ringRun(bx + col * cw, by + r * ch, cw, len * ch);
          r = end + 1;
        }
      }
      // Diagonal runs (both ↘ and ↙) — ring EACH cell of the run. Used by the
      // center diagonal (slant) doubles/triples, where the identical figures
      // sit on a diagonal and there is no straight H/V run to box.
      const diagDirs = [[1, 1], [1, -1]];
      for (const [dr, dc] of diagDirs) {
        for (let r = 0; r < gRows; r++) {
          for (let c = 0; c < gCols; c++) {
            const f = figAt(r, c);
            if (f == null) continue;
            const pr = r - dr, pc = c - dc;
            if (pr >= 0 && pr < gRows && pc >= 0 && pc < gCols && figAt(pr, pc) === f) continue; // not the run start
            let len = 1;
            while (true) {
              const nr = r + dr * len, nc = c + dc * len;
              if (nr < 0 || nr >= gRows || nc < 0 || nc >= gCols || figAt(nr, nc) !== f) break;
              len++;
            }
            if (len >= 2) {
              for (let k = 0; k < len; k++) {
                ringRun(bx + (c + dc * k) * cw, by + (r + dr * k) * ch, cw, ch);
              }
            }
          }
        }
      }
      ctx.restore();
    }

    ctx.restore();
    } // end if (!numbersOnly)

    // Numbering badges
    if (!numbering) return;

    const drawBadge = (text, centerX, centerY, size, bgOverride) => {
      // Optional tilt rotation around the badge center. Applies to ALL badges
      // (board number + row/col numbers) so the look stays consistent.
      const tiltDeg = (typeof numbering.badgeTilt === 'number') ? numbering.badgeTilt : 0;
      if (tiltDeg !== 0) {
        ctx.save();
        ctx.translate(centerX, centerY);
        ctx.rotate((tiltDeg * Math.PI) / 180);
        ctx.translate(-centerX, -centerY);
      }
      const _restoreTilt = () => { if (tiltDeg !== 0) ctx.restore(); };
      const r = size / 2;
      const noBg = numbering.badgeNoBg;
      const shape = numbering.badgeShape || 'circle';
      // When Multi-Color Borders is active, badges adopt the template-row/
      // column color for their BACKGROUND fill so they visually link to the
      // border colors. The OUTLINE always honors the user's chosen color +
      // thickness from the Board Numbering controls — this lets users add a
      // distinct outline (e.g. black) around the colored badge.
      const multiActive = !!multiColor;
      const outlineColor = numbering.badgeOutlineColor || '#000000';
      const outlineThickness = numbering.badgeOutlineThickness != null ? numbering.badgeOutlineThickness : 0;
      const bgColor = bgOverride || (multiActive ? multiColor : (numbering.badgeBgColor || '#8B4513'));

      const buildBadgePath = () => {
        if (shape === 'square') {
          const half = r * 0.9;
          const cr = half * 0.25;
          ctx.beginPath();
          ctx.moveTo(centerX - half + cr, centerY - half);
          ctx.arcTo(centerX + half, centerY - half, centerX + half, centerY + half, cr);
          ctx.arcTo(centerX + half, centerY + half, centerX - half, centerY + half, cr);
          ctx.arcTo(centerX - half, centerY + half, centerX - half, centerY - half, cr);
          ctx.arcTo(centerX - half, centerY - half, centerX + half, centerY - half, cr);
          ctx.closePath();
        } else if (shape === 'banner') {
          const bw2 = r * 2.2;
          const bh2 = r * 1.3;
          const tab = bw2 * 0.12;
          ctx.beginPath();
          ctx.moveTo(centerX - bw2 / 2, centerY - bh2 / 2);
          ctx.lineTo(centerX + bw2 / 2, centerY - bh2 / 2);
          ctx.lineTo(centerX + bw2 / 2 + tab, centerY);
          ctx.lineTo(centerX + bw2 / 2, centerY + bh2 / 2);
          ctx.lineTo(centerX - bw2 / 2, centerY + bh2 / 2);
          ctx.lineTo(centerX - bw2 / 2 - tab, centerY);
          ctx.closePath();
        } else if (shape === 'diamond') {
          ctx.beginPath();
          ctx.moveTo(centerX, centerY - r);
          ctx.lineTo(centerX + r, centerY);
          ctx.lineTo(centerX, centerY + r);
          ctx.lineTo(centerX - r, centerY);
          ctx.closePath();
        } else if (shape === 'star') {
          const spikes = 5;
          const outerR = r;
          const innerR = r * 0.45;
          ctx.beginPath();
          for (let k = 0; k < spikes * 2; k++) {
            const rad = (k * Math.PI / spikes) - Math.PI / 2;
            const dist = k % 2 === 0 ? outerR : innerR;
            const px = centerX + Math.cos(rad) * dist;
            const py = centerY + Math.sin(rad) * dist;
            if (k === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
        } else if (shape === 'hexagon') {
          ctx.beginPath();
          for (let k = 0; k < 6; k++) {
            const angle = (Math.PI / 3) * k - Math.PI / 6;
            const px = centerX + r * Math.cos(angle);
            const py = centerY + r * Math.sin(angle);
            if (k === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
          }
          ctx.closePath();
        } else {
          // circle (default)
          ctx.beginPath();
          ctx.arc(centerX, centerY, r, 0, Math.PI * 2);
        }
      };

      if (!noBg) {
        if (shape === 'image' && badgeImgRef.current) {
          // Uploaded stamp/badge background — draw the user's image scaled to
          // the full badge size, centered on (centerX, centerY). The image
          // itself supplies the visual "shape", so we skip the shape path
          // fill entirely. The number is still drawn on top by the code
          // below, honoring size + number-distance sliders like every other
          // shape option. A Custom-only "Image Size" multiplier lets the
          // user resize JUST the image without affecting the number.
          const imgScale = (numbering.badgeCustomImageSize != null ? numbering.badgeCustomImageSize : 100) / 100;
          const iw = size * imgScale;
          const ih = size * imgScale;
          const dxImg = centerX - iw / 2;
          const dyImg = centerY - ih / 2;

          // Custom-image outline: draw a tinted silhouette of the image at
          // several radial offsets so the resulting halo hugs the alpha
          // edges of the uploaded PNG. Then draw the actual image on top so
          // only the halo peeking out around the shape remains visible.
          const customOutlineW = numbering.badgeCustomOutlineWidth || 0;
          if (customOutlineW > 0) {
            const iwCeil = Math.max(1, Math.ceil(iw));
            const ihCeil = Math.max(1, Math.ceil(ih));
            const off = document.createElement('canvas');
            off.width = iwCeil;
            off.height = ihCeil;
            const octx = off.getContext('2d');
            // Draw source, then tint every non-transparent pixel with the
            // outline color via `source-in`.
            octx.drawImage(badgeImgRef.current, 0, 0, iw, ih);
            octx.globalCompositeOperation = 'source-in';
            octx.fillStyle = numbering.badgeCustomOutlineColor || '#000000';
            octx.fillRect(0, 0, iwCeil, ihCeil);
            // Stamp tinted silhouette on the main canvas at N radial
            // positions around the image origin. More steps = smoother
            // outline; 32 gives a clean anti-aliased ring at typical sizes.
            const w = Math.max(1, (customOutlineW * size) / 40);
            const steps = 32;
            for (let a = 0; a < steps; a++) {
              const theta = (a / steps) * Math.PI * 2;
              ctx.drawImage(off, dxImg + Math.cos(theta) * w, dyImg + Math.sin(theta) * w);
            }
          }

          // Draw the actual image on top of the tinted halo — the halo now
          // only shows around the alpha silhouette of the image, not as a
          // bounding-box rectangle.
          ctx.drawImage(badgeImgRef.current, dxImg, dyImg, iw, ih);
        } else {
          ctx.fillStyle = bgColor;
          buildBadgePath();
          ctx.fill();
        }
      }
      // Outline stroke (works whether or not the badge has a fill background).
      // Honors the user's Outline Thickness slider value — even when Multi-Color
      // Borders is active, so users can add a custom-colored ring around the
      // colored badge.
      const effectiveOutlineW = outlineThickness;
      if (effectiveOutlineW > 0 && shape !== 'image') {
        if (noBg) buildBadgePath();
        ctx.strokeStyle = outlineColor;
        ctx.lineWidth = (effectiveOutlineW * size) / 40;
        ctx.lineJoin = 'round';
        ctx.stroke();
      }

      // Number fill — always uses the user's Badge Text color. When
      // "No Background" is on, the Badge Background field becomes obsolete
      // (there's no fill to color), so the text should NOT swap to bgColor.
      ctx.fillStyle = numbering.badgeTextColor || '#FFFFFF';
      // Number sizing + positioning.
      //   • Non-Custom shapes → number scales with the badge (0.5 with bg,
      //     0.95 without) and is centered on the shape.
      //   • Custom (uploaded image) shape → the number gets its OWN size
      //     slider (`badgeCustomNumberSize`, 0–200% of the base ratio) and
      //     its OWN XY offset (`badgeCustomNumberShiftX/Y`, % of badge
      //     size) so the user can place the number anywhere on the stamp.
      let fs;
      let textX = centerX;
      let textY = centerY;
      if (shape === 'image') {
        const numScale = (numbering.badgeCustomNumberSize != null ? numbering.badgeCustomNumberSize : 100) / 100;
        fs = size * (noBg ? 0.95 : 0.5) * numScale;
        textX = centerX + ((numbering.badgeCustomNumberShiftX || 0) / 100) * size;
        textY = centerY + ((numbering.badgeCustomNumberShiftY || 0) / 100) * size;
      } else {
        fs = size * (noBg ? 0.95 : 0.5);
      }
      ctx.font = `bold ${fs}px ${numbering.badgeFont || 'outfit'}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      // Letter spacing for badge digits (e.g., "12" → "1 2"). Scaled the same
      // way as the badge size (size * fsScale isn't applied here because the
      // badge already runs in canvas-native coords).
      if (typeof numbering.badgeLetterSpacing === 'number' && numbering.badgeLetterSpacing !== 0) {
        try { ctx.letterSpacing = `${numbering.badgeLetterSpacing}px`; } catch (_) { /* unsupported */ }
      }
      let badgeEffect = numbering.badgeTextEffect || 'none';
      const badgeThick = (numbering.badgeTextThickness || 2) * (size / 40);
      let badgeFontOutlineColor = numbering.badgeTextOutlineColor || '#000000';
      // "Link outline to Multi-Color Borders" — when ON and Multi-Color Borders
      // resolves a per-row/column color for this board, the badge font outline
      // adopts that color. White Outline is treated as a regular outline so the
      // multi-color actually shows through instead of being forced to #FFFFFF.
      if (numbering.badgeTextOutlineLinked && multiActive) {
        badgeFontOutlineColor = multiColor;
        if (badgeEffect === 'white-outline') badgeEffect = 'outline';
        else if (badgeEffect === 'none') badgeEffect = 'outline';
      }
      drawTextWithEffect(ctx, text, textX, textY, badgeEffect, badgeThick, ctx.fillStyle, badgeFontOutlineColor);
      _restoreTilt();
    };

    const badgeSize = cw * (numbering.badgeSize || 100) / 100 * 0.6;
    const startNum = numbering.startingNum || 1;
    const offsetFactor = (numbering.badgeOffset != null ? numbering.badgeOffset : 100) / 100;
    // Manual XY shift (% of board width / height). Applied AFTER the
    // 12-slot anchor so users can tweak the badge position freely — including
    // pulling it onto the board surface for an embedded look. Applies to the
    // board-number badge only (row/col strips stay anchored).
    const badgeShiftX = ((numbering.badgeShiftX || 0) / 100) * bw;
    const badgeShiftY = ((numbering.badgeShiftY || 0) / 100) * bh;
    // Sequential numbering: each board uses (gRows + gCols) numbers. Boards
    // flagged `independentNumbering` (e.g. the one-off different-size board)
    // number from their own startingNum with NO cross-board offset.
    const boardSeqOffset = numbering.independentNumbering ? 0 : boardIndex * (gRows + gCols);

    // Board number badge — when "Highlight Board Numbers on Auto-Markup" is
    // enabled and this board has been auto-marked, override the badge bg
    // with the highlight color so it visibly pops during a live round.
    if (numbering.showBoardNum) {
      const pos = numbering.boardNumPosition || 'top';
      const { cx, cy } = computeBadgePosition(pos, bx, by, bw, bh, badgeSize, offsetFactor);
      const autoHit = highlightBoardNumOnAutoMarkup && autoMarkedBoards && autoMarkedBoards.has && autoMarkedBoards.has(board.id);
      const bgOverride = autoHit ? (highlightColor || '#FFFF00') : null;
      // Neighbors Vertical Pair Numbering — when active, override default
      // (boardIndex + startNum) with the per-board display number.
      const overrideNum = loteriaBoardDisplayNums ? loteriaBoardDisplayNums[boardIndex] : null;
      const badgeText = (overrideNum != null) ? String(overrideNum) : String(boardIndex + startNum);
      drawBadge(badgeText, cx + badgeShiftX, cy + badgeShiftY, badgeSize, bgOverride);
    }

    // Row numbers on left (vertical, sequential across boards)
    if (numbering.showRowNums && !numbersOnly) {
      for (let row = 0; row < gRows; row++) {
        const num = startNum + boardSeqOffset + row;
        drawBadge(String(num), bx - badgeSize * 0.7 * offsetFactor, by + row * ch + ch / 2, badgeSize * 0.8);
      }
    }

    // Column numbers on bottom (horizontal, sequential continuing from rows)
    if (numbering.showColNums && !numbersOnly) {
      for (let col = 0; col < gCols; col++) {
        const colOffset = numbering.showRowNums ? gRows : 0;
        const num = startNum + boardSeqOffset + colOffset + col;
        drawBadge(String(num), bx + col * cw + cw / 2, by + bh + badgeSize * 0.7 * offsetFactor, badgeSize * 0.8);
      }
    }
  }, [loteriaBorderStyle, loteriaCornerRadius, loteriaBorderWidth, loteriaGridWidth, loteriaGridColor, loteriaCenterHighlight, loteriaCenterHighlightColor, loteriaCenterHighlightThickness, loteriaCenterHighlightOffsetX, loteriaCenterHighlightOffsetY, loteriaRepeatMode, loteriaRepeatHighlight, loteriaRepeatHighlightColor, loteriaRepeatHighlightThickness, loteriaCellShape, loteriaBoardShadow, loteriaColorMode, loteriaRowColors, loteriaColColors, loteriaStripeColors, highlightBoardNumOnAutoMarkup, autoMarkedBoards, highlightColor, loteriaBoardDisplayNums, badgeImgVersion]);

  // --- MAIN DRAW ---
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const drawCanvas = async () => {
      // Reset transform stack then apply supersampling scale so all
      // subsequent drawing (in canvasWidth × canvasHeight coord space) is
      // stretched onto the higher-resolution backing (canvasWidth*superSample).
      // Result: crisp images when captured by OBS or in fullscreen mode.
      ctx.setTransform(superSample, 0, 0, superSample, 0, 0);
      // Apply filter to background layers
      ctx.filter = buildFilterString(filterSettings);

      if (skipBackground) {
        // Transparent export: wipe and skip background color + image. Boards,
        // borders, overlays, neighbors, last-card, markup all still render.
        ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      } else {
        // 1. Background color
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);

        // 2. Background image
        if (backgroundImage) {
          try {
            const img = await loadCachedImage(backgroundImage);
            const imgAr = img.width / img.height;
            const cAr = canvasWidth / canvasHeight;
            const fit = backgroundImageFit || 'cover';
            let dw, dh, dx, dy;
            if (fit === 'stretch') {
              dw = canvasWidth; dh = canvasHeight; dx = 0; dy = 0;
            } else if (fit === 'contain') {
              // Pillarbox / letterbox — entire image visible, no crop. Bars
              // show where the aspect ratios don't match (uses backgroundColor
              // as the bar color since we already filled the canvas with it).
              if (imgAr > cAr) { dw = canvasWidth; dh = dw / imgAr; dx = 0; dy = (canvasHeight - dh) / 2; }
              else              { dh = canvasHeight; dw = dh * imgAr; dx = (canvasWidth - dw) / 2; dy = 0; }
            } else {
              // 'cover' — fill canvas, crop overflow (default, legacy behavior).
              if (imgAr > cAr) { dh = canvasHeight; dw = dh * imgAr; dx = (canvasWidth - dw) / 2; dy = 0; }
              else              { dw = canvasWidth; dh = dw / imgAr; dx = 0; dy = (canvasHeight - dh) / 2; }
            }
            // Apply user zoom around the canvas center. zoom=1 → no change.
            const zoom = (typeof backgroundImageScale === 'number' && backgroundImageScale > 0)
              ? backgroundImageScale : 1.0;
            if (zoom !== 1.0) {
              const cx = canvasWidth / 2, cy = canvasHeight / 2;
              dx = cx - (cx - dx) * zoom;
              dy = cy - (cy - dy) * zoom;
              dw = dw * zoom;
              dh = dh * zoom;
            }
            ctx.drawImage(img, dx, dy, dw, dh);
          } catch (e) { console.error("Error loading background:", e); }
        }
      }

      // 3. Overlay images (filtered, no handles yet)
      //    Two-pass strategy: overlays with `aboveBoards !== true` render here
      //    (behind boards). Overlays with `aboveBoards === true` are rendered
      //    later, after all boards / neighbors / last-card / numbering, so
      //    they appear ON TOP of the template content.
      for (const overlay of overlayImages) {
        if (overlay.aboveBoards) continue;
        if (overlay.isAnimated) continue; // rendered via <video> overlay element instead
        try {
          const img = await loadCachedImage(overlay.src);
          const ox = (overlay.x * fsScale) / scale, oy = (overlay.y * fsScale) / scale;
          const ow = (overlay.width * fsScale) / scale, oh = (overlay.height * fsScale) / scale;
          ctx.save();
          ctx.globalAlpha = (overlay.opacity || 100) / 100;
          ctx.globalCompositeOperation = 'source-over';
          // Apply overlay shadow — per-overlay setting, with the legacy
          // global `overlayShadow` value used as fallback for templates
          // saved before the per-overlay control existed.
          const oShadow = overlay.shadow != null ? overlay.shadow : (overlayShadow || 'none');
          if (oShadow !== 'none') {
            const shadowSize = Math.max(ow, oh);
            const blurPct = oShadow === 'light' ? 0.03 : oShadow === 'medium' ? 0.06 : 0.1;
            const colorAlpha = oShadow === 'light' ? 0.3 : oShadow === 'medium' ? 0.5 : 0.7;
            const sx = overlay.shadowOffsetX != null ? overlay.shadowOffsetX : (overlayShadowOffsetX != null ? overlayShadowOffsetX : 2);
            const sy = overlay.shadowOffsetY != null ? overlay.shadowOffsetY : (overlayShadowOffsetY != null ? overlayShadowOffsetY : 2);
            ctx.shadowColor = `rgba(0,0,0,${colorAlpha})`;
            ctx.shadowBlur = shadowSize * blurPct;
            ctx.shadowOffsetX = shadowSize * (sx / 100);
            ctx.shadowOffsetY = shadowSize * (sy / 100);
          }
          // Outline (silhouette stroke). Drawn BEFORE the image so the image
          // sits on top and the outline only shows around the alpha edge.
          // Rotation + flip are applied around the overlay's center so the
          // outline matches the rotated/flipped silhouette exactly.
          const rot = ((overlay.rotation || 0) % 360) * Math.PI / 180;
          const fH = overlay.flipH ? -1 : 1;
          const fV = overlay.flipV ? -1 : 1;
          const cx = ox + ow / 2, cy = oy + oh / 2;
          if (rot !== 0 || fH !== 1 || fV !== 1) {
            ctx.translate(cx, cy);
            ctx.rotate(rot);
            ctx.scale(fH, fV);
            ctx.translate(-cx, -cy);
          }
          drawOverlayOutline(ctx, img, overlay, ox, oy, ow, oh, fsScale);
          ctx.drawImage(img, ox, oy, ow, oh);
          ctx.restore();
        } catch (e) { console.error("Error loading overlay:", e); }
      }

      // Reset filter for content layers
      ctx.filter = 'none';

      // Helper: compute current shake offset for a board, in canvas units.
      // Produces 3 discrete "thunk" bumps at ~300ms / 750ms / 1200ms — each
      // a quick spike (~280ms wide) that decays. Matches the hammer flash
      // CSS keyframe timing and the scheduled audio plays.
      const computeShakeOffset = (boardId) => {
        const start = shakingBoards && shakingBoards[boardId];
        if (!start) return null;
        const t = Date.now() - start;
        if (t < 0 || t > 1500) return null;
        // Build envelope: sum of 3 narrow gaussian-ish bumps at the strike times.
        const STRIKES = [300, 750, 1200];
        const WIDTH = 110; // ms — narrower = punchier
        let env = 0;
        for (const ts of STRIKES) {
          const d = (t - ts) / WIDTH;
          env += Math.exp(-d * d);
        }
        if (env < 0.01) return null;
        const amp = 14 * env * fsScale;
        // Direction: each strike pushes mostly down (positive Y) plus a small lateral wobble.
        const phase = (t / 1000) * 40;
        return { dx: Math.sin(phase) * amp * 0.4, dy: Math.abs(Math.sin(phase * 0.5)) * amp };
      };

      // 4. BINGO boards (no handles yet)
      bingoBoards.forEach((board, index) => {
        ctx.save();
        const boardNum = index + 1;
        const isIsolated = isolatedBoardNums ? isolatedBoardNums.has(boardNum) : true;
        ctx.globalAlpha = isIsolated ? (boardOpacity || 100) / 100 : 0.08;
        let bx = (board.x * fsScale) / scale, by = (board.y * fsScale) / scale;
        const shake = computeShakeOffset(board.id);
        if (shake) { bx += shake.dx; by += shake.dy; }
        const bw = (board.width * fsScale) / scale, cs = bw / 5;

        // Per-row color overrides
        const rowColors = (multiColorRows && boardRowColors && board.gridRow != null)
          ? boardRowColors[board.gridRow] : null;
        const useBoardColor = rowColors?.boardColor || boardColor;
        const useHeaderColor = rowColors?.headerColor || headerColor;
        const useHeaderLetterColor = rowColors?.headerLetterColor || headerLetterColor;
        const useCellColor = rowColors?.cellColor || cellColor;
        const useTextColor = rowColors?.textColor || textColor;

        drawBingoBoard(ctx, board.numbers, bx, by, bw, cs, boardStyle, useBoardColor,
          useHeaderColor, useHeaderLetterColor, useCellColor, useTextColor, fontFamily,
          freeSpaceNumbering ? `#${index + 1}` : freeSpaceText, freeSpaceColor, freeSpaceTextColor, transparentBoards,
          headerOpacity, cellOpacity, freeSpaceOpacity, transparentKeepHeader, transparentKeepFreeSpace);
        ctx.restore();

        // Draw highlight border for boards matching the latest Auto-Markup call.
        // When Isolate Winners is active, suppress this highlight on non-isolated boards.
        if (highlightedBoards && highlightedBoards.has(board.id) &&
            !(isolatedBoardNums && isolatedBoardNums.size > 0 && !isolatedBoardNums.has(boardNum))) {
          ctx.save();
          const boardTotalHeight = cs * 6; // header row + 5 number rows
          const hlWidth = Math.max(4, bw * 0.02);
          ctx.strokeStyle = highlightColor || '#00FF00';
          ctx.lineWidth = hlWidth;
          ctx.shadowColor = highlightColor || '#00FF00';
          ctx.shadowBlur = hlWidth * 3;
          ctx.strokeRect(bx - hlWidth / 2, by - hlWidth / 2, bw + hlWidth, boardTotalHeight + hlWidth);
          // Second pass without shadow for a crisp inner stroke
          ctx.shadowBlur = 0;
          ctx.strokeRect(bx - hlWidth / 2, by - hlWidth / 2, bw + hlWidth, boardTotalHeight + hlWidth);
          ctx.restore();
        }

        // Draw star on Free Space for "almost winner" boards (1 away from BINGO)
        if (almostWinnerBoards && almostWinnerBoards.has(board.id)) {
          ctx.save();
          ctx.globalAlpha = (starOpacity ?? 100) / 100;
          const color = starColor || '#FFD700';
          // Free space is at row=2, col=2 (center of 5x5 grid)
          const fsCx = bx + 2 * cs + cs / 2;
          const fsCy = by + cs + 2 * cs + cs / 2; // +cs for header row
          // Star fills the whole cell — outer radius touches cell edges
          const starRadius = cs * 0.48;
          const innerRadius = starRadius * 0.42;
          const spikes = 5;

          // Glow
          ctx.shadowColor = color;
          ctx.shadowBlur = cs * 0.4;
          ctx.fillStyle = color;

          // Draw star path
          ctx.beginPath();
          for (let i = 0; i < spikes * 2; i++) {
            const r = i % 2 === 0 ? starRadius : innerRadius;
            const angle = -Math.PI / 2 + (i * Math.PI / spikes);
            const sx = fsCx + Math.cos(angle) * r;
            const sy = fsCy + Math.sin(angle) * r;
            if (i === 0) ctx.moveTo(sx, sy);
            else ctx.lineTo(sx, sy);
          }
          ctx.closePath();
          ctx.fill();

          // Crisp second pass
          ctx.shadowBlur = 0;
          ctx.fill();

          // Inner highlight for depth
          ctx.fillStyle = 'rgba(255,255,255,0.35)';
          ctx.beginPath();
          for (let i = 0; i < spikes * 2; i++) {
            const r = (i % 2 === 0 ? starRadius : innerRadius) * 0.5;
            const angle = -Math.PI / 2 + (i * Math.PI / spikes);
            const sx = fsCx + Math.cos(angle) * r;
            const sy = fsCy + Math.sin(angle) * r;
            if (i === 0) ctx.moveTo(sx, sy);
            else ctx.lineTo(sx, sy);
          }
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        }
      });

      // 5b. Loteria boards
      if (gameMode === 'loteria' && loteriaBoards && loteriaBoards.length > 0) {
        loteriaBoards.forEach((board, index) => {
          ctx.save();
          const boardNum = index + 1;
          const isIsolated = isolatedBoardNums ? isolatedBoardNums.has(boardNum) : true;
          ctx.globalAlpha = isIsolated ? (boardOpacity || 100) / 100 : 0.08;
          let bx = (board.x * fsScale) / scale;
          let by = (board.y * fsScale) / scale;
          const shake = computeShakeOffset(board.id);
          if (shake) { bx += shake.dx; by += shake.dy; }
          const bw = (board.width * fsScale) / scale;
          // Per-board numbering override (Custom Vertical Pairs) — merges
          // pair-specific badge shifts on top of the global loteriaNumbering
          // so only the boards in that pair get the offset, not all boards.
          const boardNumbering = (loteriaBoardNumberingOverrides && loteriaBoardNumberingOverrides[index])
            ? { ...loteriaNumbering, ...loteriaBoardNumberingOverrides[index] }
            : loteriaNumbering;
          drawLoteriaBoard(ctx, board, bx, by, bw, board.gridSize || loteriaGridSize,
            loteriaCellColors, loteriaBorderColor, boardNumbering,
            loteriaFigureImagesRef, loteriaFigures, index);
          ctx.restore();

          // Draw highlight border for Loteria boards matching the latest Auto-Markup call.
          // When Isolate Winners is active, suppress this highlight on non-isolated boards.
          if (highlightedBoards && highlightedBoards.has(board.id) &&
              !(isolatedBoardNums && isolatedBoardNums.size > 0 && !isolatedBoardNums.has(boardNum))) {
            ctx.save();
            const _gCols = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : loteriaGridSize;
            const _gRows = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[1]) : loteriaGridSize;
            const cw = bw / _gCols;
            const ch = cw * LOTERIA_CELL_RATIO;
            const boardTotalHeight = ch * _gRows;
            const hlWidth = Math.max(4, bw * 0.02);
            ctx.strokeStyle = highlightColor || '#00FF00';
            ctx.lineWidth = hlWidth;
            ctx.shadowColor = highlightColor || '#00FF00';
            ctx.shadowBlur = hlWidth * 3;
            ctx.strokeRect(bx - hlWidth / 2, by - hlWidth / 2, bw + hlWidth, boardTotalHeight + hlWidth);
            ctx.shadowBlur = 0;
            ctx.strokeRect(bx - hlWidth / 2, by - hlWidth / 2, bw + hlWidth, boardTotalHeight + hlWidth);
            ctx.restore();
          }

          // Draw highlighted cell borders (border highlight mode)
          if (highlightedCells && highlightedCells.length > 0) {
            const _gColsH = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : loteriaGridSize;
            const cwH = bw / _gColsH;
            const chH = cwH * LOTERIA_CELL_RATIO;
            const hlW = Math.max(3, cwH * 0.04);
            ctx.save();
            ctx.strokeStyle = highlightColor || '#00FF00';
            ctx.lineWidth = hlW;
            ctx.shadowColor = highlightColor || '#00FF00';
            ctx.shadowBlur = hlW * 2;
            highlightedCells.forEach(cell => {
              if (cell.boardId === board.id) {
                const cx = bx + cell.col * cwH;
                const cy = by + cell.row * chH;
                ctx.strokeRect(cx + hlW / 2, cy + hlW / 2, cwH - hlW, chH - hlW);
              }
            });
            ctx.shadowBlur = 0;
            ctx.restore();
          }

          // Draw Almost-Winner stars on Loteria numbering badges
          if (loteriaNumbering) {
            const _gCols2 = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : loteriaGridSize;
            const _gRows2 = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[1]) : loteriaGridSize;
            const cw = bw / _gCols2;
            const ch = cw * LOTERIA_CELL_RATIO;
            const bh = ch * _gRows2;
            const badgeSize = cw * ((loteriaNumbering.badgeSize || 100) / 100) * 0.6;
            const offsetFactor = (loteriaNumbering.badgeOffset != null ? loteriaNumbering.badgeOffset : 100) / 100;

            const drawStarAt = (cx, cy, size) => {
              ctx.save();
              ctx.globalAlpha = (starOpacity ?? 100) / 100;
              const color = starColor || '#FFD700';
              const style = starStyle || 'filled';

              if (style === 'circle') {
                // Circle OUTLINE surrounding the badge — larger than the badge
                // so the number stays fully visible inside.
                const ringR = size * 0.85;
                const lineW = Math.max(2, size * 0.12);
                ctx.strokeStyle = color;
                ctx.lineWidth = lineW;
                ctx.shadowColor = color;
                ctx.shadowBlur = size * 0.4;
                ctx.beginPath();
                ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
                ctx.stroke();
                ctx.shadowBlur = 0;
                ctx.beginPath();
                ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
                ctx.stroke();
                ctx.restore();
                return;
              }

              if (style === 'star') {
                // Star OUTLINE surrounding the badge — same 5-point star as the
                // filled variant, but only stroked and larger so the badge fits
                // inside its inner radius.
                const outerR = size * 0.95;
                const innerR = outerR * 0.5;
                const spikes = 5;
                const lineW = Math.max(2, size * 0.1);
                ctx.strokeStyle = color;
                ctx.lineWidth = lineW;
                ctx.lineJoin = 'round';
                ctx.shadowColor = color;
                ctx.shadowBlur = size * 0.4;
                ctx.beginPath();
                for (let i = 0; i < spikes * 2; i++) {
                  const r = i % 2 === 0 ? outerR : innerR;
                  const angle = -Math.PI / 2 + (i * Math.PI / spikes);
                  const sx = cx + Math.cos(angle) * r;
                  const sy = cy + Math.sin(angle) * r;
                  if (i === 0) ctx.moveTo(sx, sy);
                  else ctx.lineTo(sx, sy);
                }
                ctx.closePath();
                ctx.stroke();
                ctx.shadowBlur = 0;
                ctx.stroke();
                ctx.restore();
                return;
              }

              // Default (legacy) — filled star ON the badge.
              const starRadius = size * 0.55;
              const innerR = starRadius * 0.42;
              const spikes = 5;
              ctx.shadowColor = color;
              ctx.shadowBlur = size * 0.4;
              ctx.fillStyle = color;
              ctx.beginPath();
              for (let i = 0; i < spikes * 2; i++) {
                const r = i % 2 === 0 ? starRadius : innerR;
                const angle = -Math.PI / 2 + (i * Math.PI / spikes);
                ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
              }
              ctx.closePath();
              ctx.fill();
              ctx.shadowBlur = 0;
              ctx.fill();
              ctx.fillStyle = 'rgba(255,255,255,0.35)';
              ctx.beginPath();
              for (let i = 0; i < spikes * 2; i++) {
                const r = (i % 2 === 0 ? starRadius : innerR) * 0.5;
                const angle = -Math.PI / 2 + (i * Math.PI / spikes);
                ctx.lineTo(cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
              }
              ctx.closePath();
              ctx.fill();
              ctx.restore();
            };

            // Board Number star — follows the SAME boardNumPosition that the
            // badge actually drew (incl. per-board overrides like VPN's
            // top/bottom edge placement), so the star stays glued to the badge.
            if (loteriaStarBoard && loteriaNumbering.showBoardNum && almostWinnerBoards && almostWinnerBoards.has(board.id)) {
              const override = (loteriaBoardNumberingOverrides && loteriaBoardNumberingOverrides[index]) || {};
              const pos = override.boardNumPosition || loteriaNumbering.boardNumPosition || 'top';
              const { cx: starCx, cy: starCy } = computeBadgePosition(pos, bx, by, bw, bh, badgeSize, offsetFactor);
              const sShiftXPct = (override.badgeShiftX != null ? override.badgeShiftX : (loteriaNumbering.badgeShiftX || 0));
              const sShiftYPct = (override.badgeShiftY != null ? override.badgeShiftY : (loteriaNumbering.badgeShiftY || 0));
              const sShiftX = (sShiftXPct / 100) * bw;
              const sShiftY = (sShiftYPct / 100) * bh;
              drawStarAt(starCx + sShiftX, starCy + sShiftY, badgeSize);
            }

            // Row Number stars (left) — individual row almost complete
            if (loteriaStarRowCol && loteriaNumbering.showRowNums && almostWinnerRows && almostWinnerRows[board.id]) {
              almostWinnerRows[board.id].forEach(row => {
                drawStarAt(bx - badgeSize * 0.7 * offsetFactor, by + row * ch + ch / 2, badgeSize * 0.8);
              });
            }

            // Column Number stars (bottom) — individual column almost complete
            if (loteriaStarRowCol && loteriaNumbering.showColNums && almostWinnerCols && almostWinnerCols[board.id]) {
              almostWinnerCols[board.id].forEach(col => {
                drawStarAt(bx + col * cw + cw / 2, by + bh + badgeSize * 0.7 * offsetFactor, badgeSize * 0.8);
              });
            }
          }
        });
      }

      // 4b. Neighbors Boards - draw connecting images between neighbor boards
      // Direction can be:
      //   - 'horizontal' (default): straddles the boundary between LEFT-RIGHT boards,
      //     covering rightmost N cells of left board and leftmost N cells of right board.
      //   - 'vertical': straddles the boundary between TOP-BOTTOM boards, covering
      //     bottom row N cells of top board and top row N cells of bottom board.
      //   - 'quad': straddles 4 boards arranged in a 2x2 layout at their meeting point,
      //     covering the inner-corner KxK cell block of each of the 4 boards.
      if (neighborsEnabled && gameMode === 'loteria' && loteriaBoards && loteriaBoards.length > 1 && neighborPairs && neighborPairs.length > 0) {
        neighborPairs.forEach(pair => {
          const isQuad = pair.direction === 'quad';
          const isVertical = pair.direction === 'vertical';

          const gridN = pair.gridN;
          const gridRows = pair.gridRows || pair.gridN;

          // Geometry differs by direction. We compute connX/Y/W/H + the cell rectangles
          // used by the filler / mark logic, then everything below operates on those.
          let connX, connY, connW, connH;
          const cellRectsFiller = [];
          // leftX/leftY are used later for the "if image not loaded" font-size fallback,
          // so initialise them here regardless of direction. (Quad path leaves them at 0.)
          let leftX = 0, leftY = 0;
          let cellW_L = 0, cellH_L = 0;

          // Collect the board-ids whose shake offsets should drive the connecting image
          // (averaged across all participants). Pair mode: 2 boards. Quad mode: 4 boards.
          const participantBoards = [];

          if (isQuad) {
            const tl = loteriaBoards[pair.tlBoardIdx];
            const tr = loteriaBoards[pair.trBoardIdx];
            const bl = loteriaBoards[pair.blBoardIdx];
            const br = loteriaBoards[pair.brBoardIdx];
            if (!tl || !tr || !bl || !br) return;

            // Isolate Winners: if isolation active and NONE of the 4 boards is isolated,
            // skip drawing entirely. Otherwise draw normally (partial-isolation case).
            if (isolatedBoardNums && isolatedBoardNums.size > 0) {
              const anyIn = [pair.tlBoardIdx, pair.trBoardIdx, pair.blBoardIdx, pair.brBoardIdx]
                .some(bi => isolatedBoardNums.has(bi + 1));
              if (!anyIn) return;
            }

            const k = pair.cellsPerBoard || 1;
            // Inner-corner KxK cell block for each board (in canvas-rendered px)
            const cellOf = (b) => {
              const cw = ((b.width * fsScale) / scale) / gridN;
              const ch = cw * LOTERIA_CELL_RATIO;
              return { cw, ch, bx: (b.x * fsScale) / scale, by: (b.y * fsScale) / scale };
            };
            const TL = cellOf(tl), TR = cellOf(tr), BL = cellOf(bl), BR = cellOf(br);
            const tlRect = { x: TL.bx + (gridN - k) * TL.cw, y: TL.by + (gridRows - k) * TL.ch, cw: TL.cw, ch: TL.ch };
            const trRect = { x: TR.bx,                       y: TR.by + (gridRows - k) * TR.ch, cw: TR.cw, ch: TR.ch };
            const blRect = { x: BL.bx + (gridN - k) * BL.cw, y: BL.by,                          cw: BL.cw, ch: BL.ch };
            const brRect = { x: BR.bx,                       y: BR.by,                          cw: BR.cw, ch: BR.ch };
            const right  = Math.max(trRect.x + k * trRect.cw, brRect.x + k * brRect.cw);
            const bottom = Math.max(blRect.y + k * blRect.ch, brRect.y + k * brRect.ch);
            connX = Math.min(tlRect.x, blRect.x);
            connY = Math.min(tlRect.y, trRect.y);
            connW = right - connX;
            connH = bottom - connY;

            // Build the kxk cell rects for each of the 4 boards (16 cells total when k=2).
            // Order: TL row-major, TR row-major, BL row-major, BR row-major — matches the
            // fillerFigures ordering produced by BingoGenerator.
            [tlRect, trRect, blRect, brRect].forEach(R => {
              for (let dr = 0; dr < k; dr++) {
                for (let dc = 0; dc < k; dc++) {
                  cellRectsFiller.push({ x: R.x + dc * R.cw, y: R.y + dr * R.ch, w: R.cw, h: R.ch });
                }
              }
            });
            // Use the TL-rect cell size for the "image-not-loaded" fallback font sizing.
            leftX = tlRect.x; leftY = tlRect.y; cellW_L = TL.cw; cellH_L = TL.ch;
            participantBoards.push(tl, tr, bl, br);
          } else {
            // Pair mode — for vertical, leftBoardIdx aliases topBoardIdx and rightBoardIdx
            // aliases bottomBoardIdx (per BingoGenerator.neighborPairs aliasing). Same fetch
            // works for both.
            const leftBoard = loteriaBoards[pair.leftBoardIdx];
            const rightBoard = loteriaBoards[pair.rightBoardIdx];
            if (!leftBoard || !rightBoard) return;

            // Isolate Winners: when active, skip drawing the connecting image entirely
            // if BOTH boards in the pair are not isolated.
            if (isolatedBoardNums && isolatedBoardNums.size > 0) {
              const leftIn = isolatedBoardNums.has(pair.leftBoardIdx + 1);
              const rightIn = isolatedBoardNums.has(pair.rightBoardIdx + 1);
              if (!leftIn && !rightIn) return;
            }

            // Calculate cell dimensions (in canvas-rendered pixels)
            const lbx = (leftBoard.x * fsScale) / scale;
            const lby = (leftBoard.y * fsScale) / scale;
            const lbw = (leftBoard.width * fsScale) / scale;
            const rbx = (rightBoard.x * fsScale) / scale;
            const rby = (rightBoard.y * fsScale) / scale;
            const rbw = (rightBoard.width * fsScale) / scale;
            cellW_L = lbw / gridN;
            const cellW_R = rbw / gridN;
            cellH_L = cellW_L * LOTERIA_CELL_RATIO;
            const cellH_R = cellW_R * LOTERIA_CELL_RATIO;

            let rightX, rightY;
            if (isVertical) {
              const startCol = pair.startCol || 0;
              const colsCovered = pair.colsCovered || 2;
              // Top board: bottom row, cols [startCol, startCol+colsCovered-1]
              const topX = lbx + startCol * cellW_L;
              const topY = lby + (gridRows - 1) * cellH_L;
              // Bottom board: top row, same cols
              const botX = rbx + startCol * cellW_R;
              const botY = rby;
              connX = Math.min(topX, botX);
              connY = topY;
              connW = colsCovered * Math.max(cellW_L, cellW_R);
              connH = (botY + cellH_R) - topY;
              leftX = topX; leftY = topY;
              rightX = botX; rightY = botY;
              for (let dc = 0; dc < colsCovered; dc++) {
                cellRectsFiller.push({ x: topX + dc * cellW_L, y: topY, w: cellW_L, h: cellH_L });
              }
              for (let dc = 0; dc < colsCovered; dc++) {
                cellRectsFiller.push({ x: botX + dc * cellW_R, y: botY, w: cellW_R, h: cellH_R });
              }
            } else {
              const startRow = pair.startRow || 0;
              const rowsCovered = pair.rowsCovered || 2;
              // Left board: last column, rows [startRow, startRow+rowsCovered-1]
              leftX = lbx + (gridN - 1) * cellW_L;
              leftY = lby + startRow * cellH_L;
              // Right board: first column, same rows
              rightX = rbx;
              rightY = rby + startRow * cellH_R;
              connX = leftX;
              connY = Math.min(leftY, rightY);
              connW = (rightX + cellW_R) - leftX;
              connH = rowsCovered * Math.max(cellH_L, cellH_R);
              for (let dr = 0; dr < rowsCovered; dr++) {
                cellRectsFiller.push({ x: leftX, y: leftY + dr * cellH_L, w: cellW_L, h: cellH_L });
              }
              for (let dr = 0; dr < rowsCovered; dr++) {
                cellRectsFiller.push({ x: rightX, y: rightY + dr * cellH_R, w: cellW_R, h: cellH_R });
              }
            }
            participantBoards.push(leftBoard, rightBoard);
          }

          // Apply the same shake offset as the boards underneath so the connecting image
          // moves with the board(s) being struck. Average across participants so the image
          // follows whichever participant is currently shaking.
          let shakeSumDx = 0, shakeSumDy = 0, shakeCount = 0;
          participantBoards.forEach(b => {
            const s = computeShakeOffset(b.id);
            if (s) { shakeSumDx += s.dx || 0; shakeSumDy += s.dy || 0; shakeCount++; }
          });
          const connShakeDx = shakeCount > 0 ? shakeSumDx / shakeCount : 0;
          const connShakeDy = shakeCount > 0 ? shakeSumDy / shakeCount : 0;

          ctx.save();
          if (connShakeDx || connShakeDy) {
            ctx.translate(connShakeDx, connShakeDy);
          }

          // Compute shape geometry early so we can cast a shadow BEFORE painting content.
          // Shadow is rendered first; subsequent fills/draws overwrite the interior, leaving
          // only the outer drop shadow visible on the canvas.
          let shape = neighborsShape || 'square';
          if (shape === 'star') shape = 'heart'; // legacy value migration
          const cx = connX + connW / 2;
          const cy = connY + connH / 2;
          const rx = connW / 2;
          const ry = connH / 2;
          const buildShapePath = (c) => {
            c.beginPath();
            if (shape === 'circle') {
              c.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
            } else if (shape === 'diamond') {
              c.moveTo(cx, connY);
              c.lineTo(connX + connW, cy);
              c.lineTo(cx, connY + connH);
              c.lineTo(connX, cy);
              c.closePath();
            } else if (shape === 'heart') {
              // Classic heart contained entirely WITHIN the 2-row connecting rect
              // so it doesn't bleed into the cells above/beside. Bezier controls are
              // kept at/just inside the rect edges; top dip sits below the top edge.
              c.moveTo(cx, cy - ry * 0.25);       // top-center dip, a bit below top edge
              c.bezierCurveTo(
                cx - rx * 0.55, cy - ry * 1.0,    // ctrl 1 — at top edge
                cx - rx * 1.0,  cy - ry * 0.25,   // ctrl 2 — at left edge
                cx - rx * 0.9,  cy + ry * 0.25    // end: left transition
              );
              c.bezierCurveTo(
                cx - rx * 0.7, cy + ry * 0.55,
                cx - rx * 0.2, cy + ry * 0.85,
                cx,            cy + ry             // end: bottom point
              );
              c.bezierCurveTo(
                cx + rx * 0.2, cy + ry * 0.85,
                cx + rx * 0.7, cy + ry * 0.55,
                cx + rx * 0.9, cy + ry * 0.25    // end: right transition
              );
              c.bezierCurveTo(
                cx + rx * 1.0,  cy - ry * 0.25,
                cx + rx * 0.55, cy - ry * 1.0,
                cx,             cy - ry * 0.25  // close at dip
              );
              c.closePath();
            } else if (shape === 'flower') {
              // 6-petal rosette — bigger, fatter petals with gentle bulge past the rect edges.
              const petals = 6;
              const outerR = Math.min(rx, ry);
              const innerR = outerR * 0.78;    // shallower valleys → fatter petals
              const bulge  = 1.08;              // control points bulge slightly past edges
              const total = petals * 2;
              c.moveTo(cx, cy - ry);
              for (let i = 1; i <= total; i++) {
                const ang = -Math.PI / 2 + (i * Math.PI / petals);
                const isOuter = i % 2 === 0;
                const r = isOuter ? outerR : innerR;
                const px = cx + Math.cos(ang) * r * (rx / outerR);
                const py = cy + Math.sin(ang) * r * (ry / outerR);
                const prevAng = -Math.PI / 2 + ((i - 1) * Math.PI / petals);
                const mx = cx + Math.cos((ang + prevAng) / 2) * outerR * bulge * (rx / outerR);
                const my = cy + Math.sin((ang + prevAng) / 2) * outerR * bulge * (ry / outerR);
                c.quadraticCurveTo(mx, my, px, py);
              }
              c.closePath();
            } else {
              // Square — optionally with rounded corners for 'rounded' outline style
              const outlineStyle = neighborsOutlineStyle || 'sharp';
              if (outlineStyle === 'rounded') {
                const rad = Math.min(connW, connH) * 0.12;
                const x = connX, y = connY, w = connW, h = connH;
                c.moveTo(x + rad, y);
                c.lineTo(x + w - rad, y);
                c.quadraticCurveTo(x + w, y, x + w, y + rad);
                c.lineTo(x + w, y + h - rad);
                c.quadraticCurveTo(x + w, y + h, x + w - rad, y + h);
                c.lineTo(x + rad, y + h);
                c.quadraticCurveTo(x, y + h, x, y + h - rad);
                c.lineTo(x, y + rad);
                c.quadraticCurveTo(x, y, x + rad, y);
                c.closePath();
              } else {
                c.rect(connX, connY, connW, connH);
              }
            }
          };

          // Drop shadow pass (before painting content so the shadow sits behind)
          if (neighborsShadowEnabled) {
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.55)';
            ctx.shadowBlur = Math.max(8, Math.min(connW, connH) * 0.08);
            ctx.shadowOffsetX = Math.max(2, Math.min(connW, connH) * 0.02);
            ctx.shadowOffsetY = Math.max(3, Math.min(connW, connH) * 0.035);
            buildShapePath(ctx);
            ctx.fillStyle = '#000';
            ctx.fill();
            ctx.restore();
          }

          // First, draw a background matching the canvas (covers underlying cells).
          ctx.fillStyle = backgroundColor || '#000';
          ctx.fillRect(connX, connY, connW, connH);

          // If a background image is set, redraw that portion of it clipped to the
          // connecting rect so the backdrop matches the template background seamlessly.
          if (backgroundImage) {
            const bgImg = imageCache.current.get(backgroundImage);
            if (bgImg && bgImg.complete && bgImg.naturalWidth > 0) {
              const imgAr = bgImg.width / bgImg.height;
              const cAr = canvasWidth / canvasHeight;
              const fit = backgroundImageFit || 'cover';
              let bdw, bdh, bdx, bdy;
              if (fit === 'stretch') {
                bdw = canvasWidth; bdh = canvasHeight; bdx = 0; bdy = 0;
              } else if (fit === 'contain') {
                if (imgAr > cAr) { bdw = canvasWidth; bdh = bdw / imgAr; bdx = 0; bdy = (canvasHeight - bdh) / 2; }
                else              { bdh = canvasHeight; bdw = bdh * imgAr; bdx = (canvasWidth - bdw) / 2; bdy = 0; }
              } else {
                if (imgAr > cAr) { bdh = canvasHeight; bdw = bdh * imgAr; bdx = (canvasWidth - bdw) / 2; bdy = 0; }
                else              { bdw = canvasWidth; bdh = bdw / imgAr; bdx = 0; bdy = (canvasHeight - bdh) / 2; }
              }
              // Apply user zoom (same math as the primary render).
              const bgZoom = (typeof backgroundImageScale === 'number' && backgroundImageScale > 0)
                ? backgroundImageScale : 1.0;
              if (bgZoom !== 1.0) {
                const ccx = canvasWidth / 2, ccy = canvasHeight / 2;
                bdx = ccx - (ccx - bdx) * bgZoom;
                bdy = ccy - (ccy - bdy) * bgZoom;
                bdw = bdw * bgZoom;
                bdh = bdh * bgZoom;
              }
              ctx.save();
              ctx.beginPath();
              ctx.rect(connX, connY, connW, connH);
              ctx.clip();
              ctx.drawImage(bgImg, bdx, bdy, bdw, bdh);
              ctx.restore();
            }
          }

          // Cosmetic filler: 4 distinct Loteria cards, one per underlying cell.
          // We show ONLY the card's sampled background color — not the figure artwork —
          // so the area looks like 4 colored panels behind the main shape.
          // PURELY DECORATIVE — NOT part of auto-markup or winner detection.
          if (pair.fillerFigures && pair.fillerFigures.length === cellRectsFiller.length) {
            ctx.save();
            ctx.beginPath();
            ctx.rect(connX, connY, connW, connH);
            ctx.clip();
            pair.fillerFigures.forEach((fIdx, i) => {
              const fillerData = loteriaFigures?.[fIdx];
              if (!fillerData) return;
              const fillerImg = loteriaFigureImagesRef?.current?.[fillerData.id];
              if (!fillerImg || !fillerImg.complete || fillerImg.naturalWidth === 0) return;

              // Sample and cache the card's background color from its 4 corners
              let bgColor = figureBgColorCache.current.get(fillerData.id);
              if (!bgColor) {
                try {
                  const off = document.createElement('canvas');
                  off.width = fillerImg.naturalWidth;
                  off.height = fillerImg.naturalHeight;
                  const octx = off.getContext('2d', { willReadFrequently: true });
                  octx.drawImage(fillerImg, 0, 0);
                  const samples = [
                    octx.getImageData(0, 0, 1, 1).data,
                    octx.getImageData(fillerImg.naturalWidth - 1, 0, 1, 1).data,
                    octx.getImageData(0, fillerImg.naturalHeight - 1, 1, 1).data,
                    octx.getImageData(fillerImg.naturalWidth - 1, fillerImg.naturalHeight - 1, 1, 1).data,
                  ];
                  let r = 0, g = 0, b = 0, n = 0;
                  samples.forEach(s => {
                    if (s[3] < 8) return;
                    r += s[0]; g += s[1]; b += s[2]; n++;
                  });
                  if (n > 0) {
                    bgColor = `rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`;
                    figureBgColorCache.current.set(fillerData.id, bgColor);
                  }
                } catch (err) { /* CORS-tainted — fallback to dimmed image below */ }
              }

              const r = cellRectsFiller[i];
              if (bgColor) {
                ctx.fillStyle = bgColor;
                ctx.fillRect(r.x, r.y, r.w, r.h);
              } else {
                // Fallback: dimmed full image if sampling failed
                ctx.save();
                ctx.globalAlpha = 0.4;
                ctx.drawImage(fillerImg, r.x, r.y, r.w, r.h);
                ctx.restore();
              }
            });
            ctx.restore();
          }

          // Draw the connecting figure image — fills the chosen shape completely
          // (accepts mild stretching; no letterbox / background bars visible).
          // Custom Connecting Image override: when the user has uploaded a
          // custom image, use it INSTEAD of the loteria figure. Size slider
          // scales the image around the connection center. An alpha-silhouette
          // outline (independent from the shape stroke below) traces the
          // actual image edges for a "sticker" halo effect.
          const useCustomNeighborImg = neighborsCustomImage && neighborsCustomImgRef.current;
          if (useCustomNeighborImg) {
            const imgEl = neighborsCustomImgRef.current;
            const scalePct = (neighborsCustomImageSize != null ? neighborsCustomImageSize : 100) / 100;
            const iw = connW * scalePct;
            const ih = connH * scalePct;
            const ix = connX + (connW - iw) / 2;
            const iy = connY + (connH - ih) / 2;
            // Alpha-silhouette outline BEFORE the image so it sits underneath.
            const outlineW = neighborsCustomOutlineWidth || 0;
            if (outlineW > 0) {
              const iwCeil = Math.max(1, Math.ceil(iw));
              const ihCeil = Math.max(1, Math.ceil(ih));
              const off = document.createElement('canvas');
              off.width = iwCeil;
              off.height = ihCeil;
              const octx = off.getContext('2d');
              octx.drawImage(imgEl, 0, 0, iw, ih);
              octx.globalCompositeOperation = 'source-in';
              octx.fillStyle = neighborsCustomOutlineColor || '#000000';
              octx.fillRect(0, 0, iwCeil, ihCeil);
              const w = Math.max(1, (outlineW * Math.min(connW, connH)) / 40);
              const steps = 32;
              for (let a = 0; a < steps; a++) {
                const theta = (a / steps) * Math.PI * 2;
                ctx.drawImage(off, ix + Math.cos(theta) * w, iy + Math.sin(theta) * w);
              }
            }
            ctx.drawImage(imgEl, ix, iy, iw, ih);
          } else {
            const figData = loteriaFigures?.[pair.figure];
            if (figData) {
              const imgEl = loteriaFigureImagesRef?.current?.[figData.id];
              if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
                ctx.save();
                buildShapePath(ctx);
                ctx.clip();
                ctx.drawImage(imgEl, connX, connY, connW, connH);
                ctx.restore();
              } else {
                ctx.fillStyle = '#fff';
                ctx.font = `bold ${Math.min(cellW_L, cellH_L) * 0.4}px Arial`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillText(`${figData.name || pair.figure + 1}`, connX + connW / 2, connY + connH / 2);
              }
            }
          }

          // Draw highlight border on top — follows the shape outline.
          const borderPx = Math.max(0, neighborsBorderThickness != null ? neighborsBorderThickness : 3);
          if (borderPx > 0) {
            const outlineStyle = neighborsOutlineStyle || 'sharp';
            ctx.strokeStyle = neighborsHighlightColor || '#FFD700';
            ctx.lineWidth = borderPx;
            // Line join/cap per outline style
            if (outlineStyle === 'rounded' || outlineStyle === 'soft') {
              ctx.lineJoin = 'round';
              ctx.lineCap = 'round';
            } else {
              ctx.lineJoin = 'miter';
              ctx.lineCap = 'butt';
            }
            // Glow per outline style
            ctx.shadowColor = neighborsHighlightColor || '#FFD700';
            if (outlineStyle === 'soft') {
              ctx.shadowBlur = borderPx * 8;
              // Draw a soft underlay for the halo
              ctx.save();
              ctx.globalAlpha = 0.45;
              buildShapePath(ctx);
              ctx.stroke();
              ctx.restore();
            } else {
              ctx.shadowBlur = borderPx * 3;
            }
            buildShapePath(ctx);
            ctx.stroke();
            ctx.shadowBlur = 0;
          }

          ctx.restore();
        });
      }

      // 4c. Last Card Standing — one big focal figure per board, anchored at a
      // corner and clipped to a shape. Same styling options as Neighbors.
      if (lastCardEnabled && gameMode === 'loteria' && lastCardAssignments && lastCardAssignments.length > 0) {
        lastCardAssignments.forEach(a => {
          const board = loteriaBoards[a.boardIdx];
          if (!board) return;
          if (isolatedBoardNums && isolatedBoardNums.size > 0 && !isolatedBoardNums.has(a.boardIdx + 1)) return;

          const cw = (board.width * fsScale) / scale / a.gridN;
          const ch = cw * LOTERIA_CELL_RATIO;
          const bx = (board.x * fsScale) / scale;
          const by = (board.y * fsScale) / scale;
          // Global offset applied uniformly to all focal cards. Allows positioning
          // the focal image off the board edges for a "poking out" look.
          const offX = ((lastCardOffsetX || 0) * fsScale) / scale;
          const offY = ((lastCardOffsetY || 0) * fsScale) / scale;
          // Base coverage rectangle (matches lastCardSize cells exactly).
          const baseConnX = bx + a.startCol * cw + offX;
          const baseConnY = by + a.startRow * ch + offY;
          const baseConnW = a.widthCells * cw;
          const baseConnH = a.heightCells * ch;
          // Scale the focal card around its center. 100% = original size,
          // <100 shrinks it, >100 makes it spill beyond the cells (great for a
          // "poking out" hero look).
          const lcScale = Math.max(0.5, Math.min(2, (lastCardScale != null ? lastCardScale : 100) / 100));
          const connW = baseConnW * lcScale;
          const connH = baseConnH * lcScale;
          const connX = baseConnX + (baseConnW - connW) / 2;
          const connY = baseConnY + (baseConnH - connH) / 2;

          const shape = lastCardShape || 'square';
          const cx = connX + connW / 2;
          const cy = connY + connH / 2;
          const rx = connW / 2;
          const ry = connH / 2;
          const buildPath = (c) => {
            c.beginPath();
            if (shape === 'circle') {
              c.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
            } else if (shape === 'diamond') {
              c.moveTo(cx, connY);
              c.lineTo(connX + connW, cy);
              c.lineTo(cx, connY + connH);
              c.lineTo(connX, cy);
              c.closePath();
            } else if (shape === 'heart') {
              c.moveTo(cx, cy - ry * 0.25);
              c.bezierCurveTo(cx - rx * 0.55, cy - ry * 1.0, cx - rx * 1.0, cy - ry * 0.25, cx - rx * 0.9, cy + ry * 0.25);
              c.bezierCurveTo(cx - rx * 0.7, cy + ry * 0.55, cx - rx * 0.2, cy + ry * 0.85, cx, cy + ry);
              c.bezierCurveTo(cx + rx * 0.2, cy + ry * 0.85, cx + rx * 0.7, cy + ry * 0.55, cx + rx * 0.9, cy + ry * 0.25);
              c.bezierCurveTo(cx + rx * 1.0, cy - ry * 0.25, cx + rx * 0.55, cy - ry * 1.0, cx, cy - ry * 0.25);
              c.closePath();
            } else if (shape === 'flower') {
              const petals = 6;
              const outerR = Math.min(rx, ry);
              const innerR = outerR * 0.78;
              const bulge = 1.08;
              c.moveTo(cx, cy - ry);
              for (let i = 1; i <= petals * 2; i++) {
                const ang = -Math.PI / 2 + (i * Math.PI / petals);
                const isOuter = i % 2 === 0;
                const r = isOuter ? outerR : innerR;
                const px = cx + Math.cos(ang) * r * (rx / outerR);
                const py = cy + Math.sin(ang) * r * (ry / outerR);
                const prevAng = -Math.PI / 2 + ((i - 1) * Math.PI / petals);
                const mx = cx + Math.cos((ang + prevAng) / 2) * outerR * bulge * (rx / outerR);
                const my = cy + Math.sin((ang + prevAng) / 2) * outerR * bulge * (ry / outerR);
                c.quadraticCurveTo(mx, my, px, py);
              }
              c.closePath();
            } else {
              // Square / rounded
              const os = lastCardOutlineStyle || 'sharp';
              if (os === 'rounded') {
                const rad = Math.min(connW, connH) * 0.12;
                c.moveTo(connX + rad, connY);
                c.lineTo(connX + connW - rad, connY);
                c.quadraticCurveTo(connX + connW, connY, connX + connW, connY + rad);
                c.lineTo(connX + connW, connY + connH - rad);
                c.quadraticCurveTo(connX + connW, connY + connH, connX + connW - rad, connY + connH);
                c.lineTo(connX + rad, connY + connH);
                c.quadraticCurveTo(connX, connY + connH, connX, connY + connH - rad);
                c.lineTo(connX, connY + rad);
                c.quadraticCurveTo(connX, connY, connX + rad, connY);
                c.closePath();
              } else {
                c.rect(connX, connY, connW, connH);
              }
            }
          };

          // Fill the cells hidden under the focal image with just their sampled
          // background color (no figure artwork). Drawn BEFORE the tilt transform
          // so the fill stays aligned with the board cells, not with the tilted focal.
          const boardFigs = board.figures || [];
          a.cells.forEach(cell => {
            const cellIdx = cell.row * a.gridN + cell.col;
            const figIdx = boardFigs[cellIdx];
            if (figIdx == null) return;
            const cellFigData = loteriaFigures?.[figIdx];
            if (!cellFigData) return;
            const cellImg = loteriaFigureImagesRef?.current?.[cellFigData.id];
            if (!cellImg || !cellImg.complete || cellImg.naturalWidth === 0) return;

            let bgColor = figureBgColorCache.current.get(cellFigData.id);
            if (!bgColor) {
              try {
                const off = document.createElement('canvas');
                off.width = cellImg.naturalWidth;
                off.height = cellImg.naturalHeight;
                const octx = off.getContext('2d', { willReadFrequently: true });
                octx.drawImage(cellImg, 0, 0);
                const samples = [
                  octx.getImageData(0, 0, 1, 1).data,
                  octx.getImageData(cellImg.naturalWidth - 1, 0, 1, 1).data,
                  octx.getImageData(0, cellImg.naturalHeight - 1, 1, 1).data,
                  octx.getImageData(cellImg.naturalWidth - 1, cellImg.naturalHeight - 1, 1, 1).data,
                ];
                let rr = 0, gg = 0, bb = 0, nn = 0;
                samples.forEach(s => { if (s[3] >= 8) { rr += s[0]; gg += s[1]; bb += s[2]; nn++; } });
                if (nn > 0) {
                  bgColor = `rgb(${Math.round(rr / nn)}, ${Math.round(gg / nn)}, ${Math.round(bb / nn)})`;
                  figureBgColorCache.current.set(cellFigData.id, bgColor);
                }
              } catch (err) { /* CORS-tainted — skip */ }
            }
            if (bgColor) {
              const rx0 = bx + cell.col * cw;
              const ry0 = by + cell.row * ch;
              ctx.fillStyle = bgColor;
              ctx.fillRect(rx0, ry0, cw, ch);
            }
          });

          ctx.save();

          // Apply the same shake offset as the underlying board so the
          // focal "last card" image moves in lockstep with the hammer /
          // tiger-claw effect instead of staying still while the rest of
          // the board jolts beneath it.
          const lcShake = computeShakeOffset(board.id);
          if (lcShake) {
            ctx.translate(lcShake.dx, lcShake.dy);
          }

          // Tilt: signed angle applied uniformly to all focal cards.
          const tiltDeg = typeof lastCardTilt === 'number' ? lastCardTilt : 0;
          if (tiltDeg !== 0) {
            ctx.translate(cx, cy);
            ctx.rotate((tiltDeg * Math.PI) / 180);
            ctx.translate(-cx, -cy);
          }

          // Precompute image draw bounds so the shadow (for 'square' shape) and
          // the outline can follow the ACTUAL image extent — including overflow
          // past the board border. Non-square shapes use the shape silhouette.
          const figData = loteriaFigures?.[a.figure];
          let imgDrawBounds = null;
          let imgEl = null;
          if (figData) {
            imgEl = loteriaFigureImagesRef?.current?.[figData.id];
            if (imgEl && imgEl.complete && imgEl.naturalWidth > 0) {
              const imgAR = imgEl.naturalWidth / imgEl.naturalHeight;
              const rectAR = connW / connH;
              let drawW, drawH;
              if (shape === 'square') {
                // For square shape we preserve natural aspect ratio WITHOUT
                // clipping, so the image may surpass the rect bounds.
                if (imgAR > rectAR) { drawH = connH; drawW = drawH * imgAR; }
                else                 { drawW = connW; drawH = drawW / imgAR; }
              } else {
                // For non-square shapes we cover-fit within the rect.
                if (imgAR > rectAR) { drawH = connH; drawW = drawH * imgAR; }
                else                 { drawW = connW; drawH = drawW / imgAR; }
              }
              const drawX = connX + (connW - drawW) / 2;
              const drawY = connY + (connH - drawH) / 2;
              imgDrawBounds = { x: drawX, y: drawY, w: drawW, h: drawH };
            }
          }

          // Drop shadow (shape-aware). For 'square' it follows the actual image
          // bounds. For other shapes it follows the shape silhouette. Rendered
          // BEFORE the image so only the offset halo remains visible.
          const drawShadowNow = () => {
            if (!lastCardShadowEnabled) return;
            ctx.save();
            ctx.shadowColor = 'rgba(0,0,0,0.55)';
            ctx.shadowBlur = Math.max(8, Math.min(connW, connH) * 0.08);
            ctx.shadowOffsetX = Math.max(2, Math.min(connW, connH) * 0.02);
            ctx.shadowOffsetY = Math.max(3, Math.min(connW, connH) * 0.035);
            if (shape === 'square' && imgDrawBounds) {
              ctx.fillStyle = '#000';
              ctx.fillRect(imgDrawBounds.x, imgDrawBounds.y, imgDrawBounds.w, imgDrawBounds.h);
            } else {
              buildPath(ctx);
              ctx.fillStyle = '#000';
              ctx.fill();
            }
            ctx.restore();
          };

          if (lastCardShadowEnabled) drawShadowNow();

          // Figure image — preserve natural aspect ratio. For square shape we do
          // NOT clip, so the image can surpass the board border when needed.
          // For non-square shapes we clip to the shape silhouette.
          if (figData) {
            ctx.save();
            if (shape !== 'square') {
              buildPath(ctx);
              ctx.clip();
            }
            if (imgEl && imgEl.complete && imgEl.naturalWidth > 0 && imgDrawBounds) {
              ctx.drawImage(imgEl, imgDrawBounds.x, imgDrawBounds.y, imgDrawBounds.w, imgDrawBounds.h);
            } else {
              ctx.fillStyle = '#222';
              ctx.fillRect(connX, connY, connW, connH);
              ctx.fillStyle = '#fff';
              ctx.font = `bold ${Math.min(cw, ch) * 0.4}px Arial`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(`${figData.name || a.figure + 1}`, cx, cy);
            }
            ctx.restore();
          }

          // Highlight outline — for 'square' shape, the outline wraps the ACTUAL
          // image bounds (including overflow past the board border). For non-square
          // shapes, the outline follows the shape silhouette.
          const borderPx = Math.max(0, lastCardBorderThickness != null ? lastCardBorderThickness : 3);
          if (borderPx > 0) {
            const os = lastCardOutlineStyle || 'sharp';
            ctx.strokeStyle = lastCardHighlightColor || '#FFD700';
            ctx.lineWidth = borderPx;
            if (os === 'rounded' || os === 'soft') { ctx.lineJoin = 'round'; ctx.lineCap = 'round'; }
            else { ctx.lineJoin = 'miter'; ctx.lineCap = 'butt'; }
            ctx.shadowColor = lastCardHighlightColor || '#FFD700';

            // Build the stroke path based on shape + available image bounds.
            const strokeImagePath = (c) => {
              if (shape === 'square' && imgDrawBounds) {
                if (os === 'rounded') {
                  const rad = Math.min(imgDrawBounds.w, imgDrawBounds.h) * 0.12;
                  const x = imgDrawBounds.x, y = imgDrawBounds.y, w = imgDrawBounds.w, h = imgDrawBounds.h;
                  c.beginPath();
                  c.moveTo(x + rad, y);
                  c.lineTo(x + w - rad, y);
                  c.quadraticCurveTo(x + w, y, x + w, y + rad);
                  c.lineTo(x + w, y + h - rad);
                  c.quadraticCurveTo(x + w, y + h, x + w - rad, y + h);
                  c.lineTo(x + rad, y + h);
                  c.quadraticCurveTo(x, y + h, x, y + h - rad);
                  c.lineTo(x, y + rad);
                  c.quadraticCurveTo(x, y, x + rad, y);
                  c.closePath();
                } else {
                  c.beginPath();
                  c.rect(imgDrawBounds.x, imgDrawBounds.y, imgDrawBounds.w, imgDrawBounds.h);
                }
              } else {
                buildPath(c);
              }
            };

            if (os === 'soft') {
              ctx.shadowBlur = borderPx * 8;
              ctx.save();
              ctx.globalAlpha = 0.45;
              strokeImagePath(ctx);
              ctx.stroke();
              ctx.restore();
            } else {
              ctx.shadowBlur = borderPx * 3;
            }
            strokeImagePath(ctx);
            ctx.stroke();
            ctx.shadowBlur = 0;
          }

          ctx.restore();
        });
      }

      // 4c-2. Board-number badge re-stamp pass — when Last Card Standing is
      // enabled, the focal image (above) may completely cover the badge if
      // the user placed the badge with Free-Position shift sliders. Re-draw
      // ONLY the board-number badge for every board on top of the focal
      // images so the number stays visible. Numbers-only mode skips all
      // cell / border / figure / row-col badge rendering.
      if (lastCardEnabled && loteriaNumbering && loteriaNumbering.showBoardNum && lastCardAssignments && lastCardAssignments.length > 0) {
        loteriaBoards.forEach((board, index) => {
          const bx = (board.x * fsScale) / scale;
          const by = (board.y * fsScale) / scale;
          const bw = (board.width * fsScale) / scale;
          const boardNumbering = (loteriaBoardNumberingOverrides && loteriaBoardNumberingOverrides[index])
            ? { ...loteriaNumbering, ...loteriaBoardNumberingOverrides[index] }
            : loteriaNumbering;
          drawLoteriaBoard(ctx, board, bx, by, bw, board.gridSize || loteriaGridSize,
            loteriaCellColors, loteriaBorderColor, boardNumbering,
            loteriaFigureImagesRef, loteriaFigures, index, true);
        });
      }

      // 4c-3. Almost-Winner star re-stamp pass — the numbersOnly badge re-render
      // above (Last Card Standing) paints the board-number badge over the star
      // that was drawn back in the main Loteria pass. Re-draw the star here so
      // it stays glued to (and on top of) the badge — critical when the user
      // has Free-Positioned the badge onto the board surface. Only runs when
      // LCS is active (same condition as the badge re-stamp above), otherwise
      // the primary star pass at line 1290 is already the top layer.
      if (lastCardEnabled && lastCardAssignments && lastCardAssignments.length > 0 &&
          gameMode === 'loteria' && loteriaBoards && loteriaBoards.length > 0 &&
          loteriaStarBoard && loteriaNumbering && loteriaNumbering.showBoardNum &&
          almostWinnerBoards && almostWinnerBoards.size > 0) {
        const _gColsS = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[0]) : loteriaGridSize;
        const _gRowsS = typeof loteriaGridSize === 'string' && loteriaGridSize.includes('x') ? parseInt(loteriaGridSize.split('x')[1]) : loteriaGridSize;
        loteriaBoards.forEach((board, index) => {
          if (!almostWinnerBoards.has(board.id)) return;
          const bx = (board.x * fsScale) / scale;
          const by = (board.y * fsScale) / scale;
          const bw = (board.width * fsScale) / scale;
          const cw = bw / _gColsS;
          const ch = cw * LOTERIA_CELL_RATIO;
          const bh = ch * _gRowsS;
          const badgeSize = cw * ((loteriaNumbering.badgeSize || 100) / 100) * 0.6;
          const offsetFactor = (loteriaNumbering.badgeOffset != null ? loteriaNumbering.badgeOffset : 100) / 100;
          const override = (loteriaBoardNumberingOverrides && loteriaBoardNumberingOverrides[index]) || {};
          const pos = override.boardNumPosition || loteriaNumbering.boardNumPosition || 'top';
          const { cx: starCx, cy: starCy } = computeBadgePosition(pos, bx, by, bw, bh, badgeSize, offsetFactor);
          const sShiftXPct = (override.badgeShiftX != null ? override.badgeShiftX : (loteriaNumbering.badgeShiftX || 0));
          const sShiftYPct = (override.badgeShiftY != null ? override.badgeShiftY : (loteriaNumbering.badgeShiftY || 0));
          const sShiftX = (sShiftXPct / 100) * bw;
          const sShiftY = (sShiftYPct / 100) * bh;
          // Inline star draw (matches drawStarAt from main pass)
          const fx = starCx + sShiftX;
          const fy = starCy + sShiftY;
          const size = badgeSize;
          ctx.save();
          ctx.globalAlpha = (starOpacity ?? 100) / 100;
          const color = starColor || '#FFD700';
          const starRadius = size * 0.55;
          const innerR = starRadius * 0.42;
          const spikes = 5;
          ctx.shadowColor = color;
          ctx.shadowBlur = size * 0.4;
          ctx.fillStyle = color;
          ctx.beginPath();
          for (let i = 0; i < spikes * 2; i++) {
            const r = i % 2 === 0 ? starRadius : innerR;
            const angle = -Math.PI / 2 + (i * Math.PI / spikes);
            ctx.lineTo(fx + Math.cos(angle) * r, fy + Math.sin(angle) * r);
          }
          ctx.closePath();
          ctx.fill();
          ctx.shadowBlur = 0;
          ctx.fill();
          ctx.fillStyle = 'rgba(255,255,255,0.35)';
          ctx.beginPath();
          for (let i = 0; i < spikes * 2; i++) {
            const r = (i % 2 === 0 ? starRadius : innerR) * 0.5;
            const angle = -Math.PI / 2 + (i * Math.PI / spikes);
            ctx.lineTo(fx + Math.cos(angle) * r, fy + Math.sin(angle) * r);
          }
          ctx.closePath();
          ctx.fill();
          ctx.restore();
        });
      }

      // 3b. Overlay images marked "On top of Boards" — rendered AFTER all
      // boards / neighbors / last-card / numbering so they cover the template
      // content. Markup still renders above them so auto-marks / drawings
      // remain visible on top of the overlay.
      for (const overlay of overlayImages) {
        if (!overlay.aboveBoards) continue;
        if (overlay.isAnimated) continue; // rendered via <video> overlay element instead
        try {
          const img = await loadCachedImage(overlay.src);
          const ox = (overlay.x * fsScale) / scale, oy = (overlay.y * fsScale) / scale;
          const ow = (overlay.width * fsScale) / scale, oh = (overlay.height * fsScale) / scale;
          ctx.save();
          ctx.globalAlpha = (overlay.opacity || 100) / 100;
          ctx.globalCompositeOperation = 'source-over';
          const oShadow = overlay.shadow != null ? overlay.shadow : (overlayShadow || 'none');
          if (oShadow !== 'none') {
            const shadowSize = Math.max(ow, oh);
            const blurPct = oShadow === 'light' ? 0.03 : oShadow === 'medium' ? 0.06 : 0.1;
            const colorAlpha = oShadow === 'light' ? 0.3 : oShadow === 'medium' ? 0.5 : 0.7;
            const sx2 = overlay.shadowOffsetX != null ? overlay.shadowOffsetX : (overlayShadowOffsetX != null ? overlayShadowOffsetX : 2);
            const sy2 = overlay.shadowOffsetY != null ? overlay.shadowOffsetY : (overlayShadowOffsetY != null ? overlayShadowOffsetY : 2);
            ctx.shadowColor = `rgba(0,0,0,${colorAlpha})`;
            ctx.shadowBlur = shadowSize * blurPct;
            ctx.shadowOffsetX = shadowSize * (sx2 / 100);
            ctx.shadowOffsetY = shadowSize * (sy2 / 100);
          }
          const rot2 = ((overlay.rotation || 0) % 360) * Math.PI / 180;
          const fH2 = overlay.flipH ? -1 : 1;
          const fV2 = overlay.flipV ? -1 : 1;
          const cx2 = ox + ow / 2, cy2 = oy + oh / 2;
          if (rot2 !== 0 || fH2 !== 1 || fV2 !== 1) {
            ctx.translate(cx2, cy2);
            ctx.rotate(rot2);
            ctx.scale(fH2, fV2);
            ctx.translate(-cx2, -cy2);
          }
          drawOverlayOutline(ctx, img, overlay, ox, oy, ow, oh, fsScale);
          ctx.drawImage(img, ox, oy, ow, oh);
          ctx.restore();
        } catch (e) { console.error("Error loading above-boards overlay:", e); }
      }

      if (markupElements) {
        markupElements.forEach(el => {
          if (isolatedBoardNums && el.boardNum && !isolatedBoardNums.has(el.boardNum)) return;
          drawMarkupElement(ctx, el, fsScale);
        });
      }
      if (activeDrawing) {
        if (activeDrawing.type !== 'eraser') drawMarkupElement(ctx, activeDrawing, fsScale);
      }

      // Selection outline for the currently-selected markup element (Select tool)
      if (selectedMarkupId && markupElements) {
        const sel = markupElements.find(el => el.id === selectedMarkupId);
        if (sel) {
          // Compute the element's bounding box (in canvas-rendered pixels)
          let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
          if (sel.points && sel.points.length > 0) {
            sel.points.forEach(p => {
              const x = (p.x * fsScale) / scale, y = (p.y * fsScale) / scale;
              if (x < minX) minX = x; if (x > maxX) maxX = x;
              if (y < minY) minY = y; if (y > maxY) maxY = y;
            });
          } else if (sel.startPoint && sel.endPoint) {
            const sx = (sel.startPoint.x * fsScale) / scale, sy = (sel.startPoint.y * fsScale) / scale;
            const ex = (sel.endPoint.x * fsScale) / scale, ey = (sel.endPoint.y * fsScale) / scale;
            minX = Math.min(sx, ex); maxX = Math.max(sx, ex);
            minY = Math.min(sy, ey); maxY = Math.max(sy, ey);
          }
          if (isFinite(minX)) {
            const pad = 6 / scale;
            ctx.save();
            ctx.strokeStyle = 'rgba(168, 85, 247, 0.95)'; // purple-500
            ctx.lineWidth = 2 / scale;
            ctx.setLineDash([6 / scale, 4 / scale]);
            ctx.strokeRect(minX - pad, minY - pad, (maxX - minX) + pad * 2, (maxY - minY) + pad * 2);
            ctx.restore();

            // 8 resize handles on circle/square/stamp shapes (4 corners + 4 edges).
            // Lets the user widen or stretch the shape by dragging.
            if (sel.type === 'circle' || sel.type === 'square' || sel.type === 'stamp') {
              const midX = (minX + maxX) / 2, midY = (minY + maxY) / 2;
              const handles = [
                [minX - pad, minY - pad], [midX, minY - pad], [maxX + pad, minY - pad],
                [minX - pad, midY],                            [maxX + pad, midY],
                [minX - pad, maxY + pad], [midX, maxY + pad], [maxX + pad, maxY + pad],
              ];
              const HANDLE_SIZE = 8 / scale;
              ctx.save();
              ctx.fillStyle = 'rgba(168, 85, 247, 1)';
              ctx.strokeStyle = '#ffffff';
              ctx.lineWidth = 1.5 / scale;
              handles.forEach(([hx, hy]) => {
                ctx.beginPath();
                ctx.rect(hx - HANDLE_SIZE / 2, hy - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
                ctx.fill();
                ctx.stroke();
              });
              ctx.restore();
            }
          }
        }
      }

      // Eraser cursor visual
      if (activeMarkupTool === 'eraser' && mousePos.current) {
        ctx.save();
        ctx.strokeStyle = 'rgba(255,0,0,0.6)';
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 4]);
        const eSize = (eraserThickness * 3 * fsScale) / scale;
        const ex = (mousePos.current.x * fsScale) / scale;
        const ey = (mousePos.current.y * fsScale) / scale;
        ctx.beginPath();
        ctx.arc(ex, ey, eSize, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
      }

      // 6. Emojis
      if (emojis) {
        emojis.forEach(em => {
          const ex = (em.x * fsScale) / scale, ey = (em.y * fsScale) / scale;
          const fontSize = (em.size * fsScale) / scale;
          ctx.font = `${fontSize}px sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(em.emoji, ex, ey);
        });
      }

      // 7. Watermark
      if (watermarkEnabled && watermarkText) {
        drawWatermark(ctx, watermarkText, watermarkOpacity, watermarkColor, watermarkFont, canvasWidth, canvasHeight);
      }

      // 7b. Custom Template Text — drawn at the user-positioned location with
      // optional outline. Default position is top-center if X/Y are null.
      // templateTextX/Y are stored in DISPLAY space (matching getMousePos).
      // Convert to canvas-native (scale factor) for drawing.
      if (templateText && templateText.trim() !== '') {
        const ttFamily = WATERMARK_FONT_MAP[templateTextFont] || WATERMARK_FONT_MAP['outfit'];
        const ttSize = ((templateTextSize || 64) * fsScale) / scale;
        ctx.save();
        ctx.font = `bold ${ttSize}px ${ttFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        const defaultX = canvasWidth / 2;
        const defaultY = ttSize * 0.25;
        // templateTextX/Y are in NORMAL-DISPLAY space (matching getMousePos which
        // divides by fsScale). Convert to canvas-internal: multiply by fsScale
        // (apply current display) then divide by scale (display → canvas).
        const xMid = (templateTextX != null) ? (templateTextX * fsScale) / scale : defaultX;
        const yTop = (templateTextY != null) ? (templateTextY * fsScale) / scale : defaultY;
        ctx.lineJoin = 'round';
        ctx.miterLimit = 2;
        // Letter spacing — uses modern ctx.letterSpacing API. Scaled to canvas
        // space the same way fonts are: (value * fsScale) / scale.
        if (typeof templateTextLetterSpacing === 'number' && templateTextLetterSpacing !== 0) {
          try { ctx.letterSpacing = `${(templateTextLetterSpacing * fsScale) / scale}px`; } catch (_) { /* unsupported */ }
        }
        // Opacity (0-100%) — uniform alpha for fill + outline.
        if (typeof templateTextOpacity === 'number' && templateTextOpacity < 100) {
          ctx.globalAlpha *= Math.max(0, Math.min(100, templateTextOpacity)) / 100;
        }
        if (templateTextOutlineEnabled && templateTextOutlineWidth > 0) {
          ctx.strokeStyle = templateTextOutlineColor || '#FFFFFF';
          ctx.lineWidth = ((templateTextOutlineWidth || 4) * fsScale) / scale * 2;
          ctx.strokeText(templateText, xMid, yTop);
        }
        ctx.fillStyle = templateTextColor || '#000000';
        ctx.fillText(templateText, xMid, yTop);
        ctx.restore();
      }

      // 7c. Extra template texts (unlimited list). Same render as above, but
      // each item carries its own font/color/outline/size/position.
      if (Array.isArray(extraTemplateTexts) && extraTemplateTexts.length > 0) {
        extraTemplateTexts.forEach(t => {
          if (!t || !t.text || !t.text.trim()) return;
          // Resolve font: t.font may be a font id (preferred — `'fredoka'`)
          // or a legacy CSS family string from a buggy older session
          // (e.g., `'Fredoka', sans-serif`). Use raw if it's a family, look up
          // if it's an id, fall back to outfit otherwise.
          let ttFamily2;
          if (typeof t.font === 'string' && (t.font.includes(',') || t.font.includes("'") || t.font.includes('"'))) {
            ttFamily2 = t.font;
          } else {
            ttFamily2 = WATERMARK_FONT_MAP[t.font] || WATERMARK_FONT_MAP['outfit'];
          }
          const ttSize2 = ((t.size || 64) * fsScale) / scale;
          ctx.save();
          ctx.font = `bold ${ttSize2}px ${ttFamily2}`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'top';
          ctx.lineJoin = 'round';
          ctx.miterLimit = 2;
          if (typeof t.letterSpacing === 'number' && t.letterSpacing !== 0) {
            try { ctx.letterSpacing = `${(t.letterSpacing * fsScale) / scale}px`; } catch (_) { /* unsupported */ }
          }
          if (typeof t.opacity === 'number' && t.opacity < 100) {
            ctx.globalAlpha *= Math.max(0, Math.min(100, t.opacity)) / 100;
          }
          const dX = canvasWidth / 2;
          const dY = ttSize2 * 0.25;
          const xM = (t.x != null) ? (t.x * fsScale) / scale : dX;
          const yT = (t.y != null) ? (t.y * fsScale) / scale : dY;
          if (t.outlineEnabled && (t.outlineWidth || 0) > 0) {
            ctx.strokeStyle = t.outlineColor || '#FFFFFF';
            ctx.lineWidth = ((t.outlineWidth || 4) * fsScale) / scale * 2;
            ctx.strokeText(t.text, xM, yT);
          }
          ctx.fillStyle = t.color || '#000000';
          ctx.fillText(t.text, xM, yT);
          ctx.restore();
        });
      }

      // 8. Selection handles (no filter). Skipped in fullscreen ONLY for
      //    Owner accounts — they get a clean presenter view. Subscribers/
      //    Demo keep the legacy behavior with visible selection outlines.
      if (!(bgVideoIsOwner && isFullscreen)) {
      // Overlay handles
      for (const overlay of overlayImages) {
        if (selectedOverlayId === overlay.id) {
          const ox = (overlay.x * fsScale) / scale, oy = (overlay.y * fsScale) / scale;
          const ow = (overlay.width * fsScale) / scale, oh = (overlay.height * fsScale) / scale;
          ctx.strokeStyle = "#FF6B6B"; ctx.lineWidth = 4;
          ctx.setLineDash([10, 5]);
          ctx.strokeRect(ox - 2, oy - 2, ow + 4, oh + 4);
          ctx.setLineDash([]);
          const hs = 12; ctx.fillStyle = "#FF6B6B";
          ctx.fillRect(ox - hs/2, oy - hs/2, hs, hs);
          ctx.fillRect(ox + ow - hs/2, oy - hs/2, hs, hs);
          ctx.fillRect(ox - hs/2, oy + oh - hs/2, hs, hs);
          ctx.fillRect(ox + ow - hs/2, oy + oh - hs/2, hs, hs);
        }
      }
      // Board handles
      bingoBoards.forEach(board => {
        if (selectedBoardId === board.id) {
          const bx = (board.x * fsScale) / scale, by = (board.y * fsScale) / scale;
          const bw = (board.width * fsScale) / scale, th = (bw / 5) * 6;
          ctx.strokeStyle = "#4ECDC4"; ctx.lineWidth = 4;
          ctx.setLineDash([10, 5]);
          ctx.strokeRect(bx - 4, by - 4, bw + 8, th + 8);
          ctx.setLineDash([]);
          const hs = 12; ctx.fillStyle = "#4ECDC4";
          ctx.fillRect(bx - hs/2, by - hs/2, hs, hs);
          ctx.fillRect(bx + bw - hs/2, by - hs/2, hs, hs);
          ctx.fillRect(bx - hs/2, by + th - hs/2, hs, hs);
          ctx.fillRect(bx + bw - hs/2, by + th - hs/2, hs, hs);
        }
      });
      // Loteria board handles
      if (loteriaBoards) {
        loteriaBoards.forEach(board => {
          if (selectedBoardId === board.id) {
            const bx = (board.x * fsScale) / scale, by = (board.y * fsScale) / scale;
            const bw = (board.width * fsScale) / scale, bh = bw * LOTERIA_CELL_RATIO;
            ctx.strokeStyle = "#4ECDC4"; ctx.lineWidth = 4;
            ctx.setLineDash([10, 5]);
            ctx.strokeRect(bx - 4, by - 4, bw + 8, bh + 8);
            ctx.setLineDash([]);
            const hs = 12; ctx.fillStyle = "#4ECDC4";
            ctx.fillRect(bx - hs/2, by - hs/2, hs, hs);
            ctx.fillRect(bx + bw - hs/2, by - hs/2, hs, hs);
            ctx.fillRect(bx - hs/2, by + bh - hs/2, hs, hs);
            ctx.fillRect(bx + bw - hs/2, by + bh - hs/2, hs, hs);
          }
        });
      }
      // Emoji handles
      if (emojis) {
        emojis.forEach(em => {
          if (selectedEmojiId === em.id) {
            const ex = (em.x * fsScale) / scale, ey = (em.y * fsScale) / scale;
            const half = (em.size * fsScale) / scale / 2;
            ctx.strokeStyle = "#FDE047"; ctx.lineWidth = 3;
            ctx.setLineDash([8, 4]);
            ctx.strokeRect(ex - half - 4, ey - half - 4, half * 2 + 8, half * 2 + 8);
            ctx.setLineDash([]);
          }
        });
      }
      } // end owner-only fullscreen selection-handles skip

      // 9. Snap alignment guides (bright blue lines)
      if (snapGuides.length > 0 && isDragging) {
        ctx.save();
        ctx.strokeStyle = '#0099FF';
        ctx.lineWidth = 1.5;
        ctx.setLineDash([6, 3]);
        snapGuides.forEach(guide => {
          const p = (guide.pos * fsScale) / scale;
          ctx.beginPath();
          if (guide.type === 'v') {
            ctx.moveTo(p, 0);
            ctx.lineTo(p, canvasHeight);
          } else {
            ctx.moveTo(0, p);
            ctx.lineTo(canvasWidth, p);
          }
          ctx.stroke();
        });
        ctx.setLineDash([]);
        ctx.restore();
      }

      // 10. Prize Dots — horizontal row of colored circles with a text inside
      //     and an auto label above (Rd. 1, Rd. 2, …, NP for the last one).
      if (prizeDots && prizeDots.enabled && prizeDots.count > 0) {
        const pd = prizeDots;
        const r = pd.dotSize || 60;
        const gap = pd.spacing || 40;
        const totalWidth = pd.count * (r * 2) + Math.max(0, pd.count - 1) * gap;
        // Default anchor: horizontally centered, near bottom.
        const groupX = (pd.x != null ? pd.x : (canvasWidth / 2 - totalWidth / 2));
        const groupY = (pd.y != null ? pd.y : (canvasHeight * 0.88));
        for (let i = 0; i < pd.count; i++) {
          const dotX = groupX + r + i * (r * 2 + gap);
          const dotY = groupY;
          // Independent outlines: labelOutline* applies to the Rd./NP label
          // above; textOutline* applies to the inside-circle text.
          const labelOutlineW = pd.labelOutlineWidth != null ? pd.labelOutlineWidth : 0;
          const labelOutlineCol = pd.labelOutlineColor || '#000000';
          const textOutlineW = pd.textOutlineWidth != null ? pd.textOutlineWidth : 0;
          const textOutlineCol = pd.textOutlineColor || '#000000';
          // Label above
          if (pd.showLabels) {
            const isNP = i === pd.count - 1;
            const label = isNP ? 'NP' : `Rd. ${i + 1}`;
            const labelSize = pd.labelSize != null ? pd.labelSize : Math.max(14, r * 0.4);
            ctx.save();
            ctx.font = `bold ${labelSize}px ${pd.labelFont || 'outfit'}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            if (labelOutlineW > 0) {
              ctx.strokeStyle = labelOutlineCol;
              ctx.lineWidth = labelOutlineW;
              ctx.lineJoin = 'round';
              ctx.strokeText(label, dotX, dotY - r - 8);
            }
            ctx.fillStyle = pd.labelColor || '#000000';
            ctx.fillText(label, dotX, dotY - r - 8);
            ctx.restore();
          }
          // Circle — optional stroke outline around each dot. Width 0 = no outline.
          ctx.save();
          ctx.fillStyle = pd.dotColor || '#0F3B7C';
          ctx.beginPath();
          ctx.arc(dotX, dotY, r, 0, Math.PI * 2);
          ctx.fill();
          const dotOutlineW = pd.dotOutlineWidth != null ? pd.dotOutlineWidth : 0;
          if (dotOutlineW > 0) {
            ctx.strokeStyle = pd.dotOutlineColor || '#000000';
            ctx.lineWidth = dotOutlineW;
            ctx.stroke();
          }
          ctx.restore();
          // Text inside circle
          const insideText = (pd.texts && pd.texts[i] != null) ? pd.texts[i] : 'Text';
          if (insideText && String(insideText).trim() !== '') {
            const textSize = pd.textSize != null ? pd.textSize : Math.max(12, r * 0.5);
            ctx.save();
            ctx.font = `bold ${textSize}px ${pd.font || 'outfit'}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            if (textOutlineW > 0) {
              ctx.strokeStyle = textOutlineCol;
              ctx.lineWidth = textOutlineW;
              ctx.lineJoin = 'round';
              ctx.strokeText(String(insideText), dotX, dotY);
            }
            ctx.fillStyle = pd.textColor || '#FFFFFF';
            ctx.fillText(String(insideText), dotX, dotY);
            ctx.restore();
          }
        }
      }
    };

    // rAF-batched draw: coalesce rapid state changes (dragging sliders,
    // typing in inputs, cascading updates from a single event) into ONE
    // draw per animation frame. Without this every state change kicks off
    // a full canvas repaint, which iOS Safari struggles with on large
    // (150-DPI+ letter-size) canvases. rAF caps redraws at ~60fps and
    // gives Safari a chance to composite between frames.
    let cancelled = false;
    const rafId = requestAnimationFrame(() => {
      if (cancelled) return;
      drawCanvas().catch(console.error);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [
    canvasWidth, canvasHeight, backgroundColor, backgroundImage, overlayImages, selectedOverlayId,
    bingoBoards, selectedBoardId, boardStyle, boardColor, headerColor, headerLetterColor, cellColor, textColor, fontFamily, headerFontFamily,
    freeSpaceText, freeSpaceColor, freeSpaceTextColor, freeSpaceNumbering, freeSpaceTextEffect, freeSpaceTextThickness, boardOpacity, isolatedBoardNums, neighborsEnabled, neighborsPosition, neighborsDirection, neighborsHighlightColor, neighborsShape, neighborsBorderThickness, neighborsShadowEnabled, neighborsOutlineStyle, neighborsCustomImage, neighborsCustomImageSize, neighborsCustomOutlineWidth, neighborsCustomOutlineColor, neighborsCustomImgVersion, neighborPairs, lastCardEnabled, lastCardShape, lastCardHighlightColor, lastCardBorderThickness, lastCardShadowEnabled, lastCardOutlineStyle, lastCardTilt, lastCardOffsetX, lastCardOffsetY, lastCardScale, lastCardAssignments, transparentBoards,
    headerOpacity, cellOpacity, freeSpaceOpacity, transparentKeepHeader, transparentKeepFreeSpace, multiColorRows, boardRowColors,
    watermarkEnabled, watermarkText, watermarkOpacity, watermarkColor, watermarkFont, scale,
    templateTextEnabled, templateText, templateTextFont, templateTextColor,
    templateTextOutlineEnabled, templateTextOutlineColor, templateTextOutlineWidth, templateTextSize,
    templateTextX, templateTextY, templateTextLetterSpacing, templateTextOpacity, extraTemplateTexts, prizeDots,
    markupElements, activeDrawing, emojis, selectedEmojiId, filterSettings, fsScale,
    buildFilterString, loadCachedImage, highlightedBoards, highlightedCells, highlightColor,
    shakingBoards, _shakeTick,
    snapGuides, isDragging, almostWinnerBoards, almostWinnerRows, almostWinnerCols,
    loteriaStarBoard, loteriaStarRowCol, starColor, starOpacity, starStyle,
    gameMode, loteriaBoards, loteriaFigures, loteriaGridSize,
    loteriaCellColors, loteriaBorderColor, loteriaGridColor, loteriaCellShape, loteriaNumbering,
    loteriaCenterHighlight, loteriaCenterHighlightColor,
    loteriaRepeatMode, loteriaRepeatHighlight, loteriaRepeatHighlightColor,
    loteriaCenterHighlightThickness, loteriaRepeatHighlightThickness,
    loteriaCenterHighlightOffsetX, loteriaCenterHighlightOffsetY,
    loteriaColorMode, loteriaRowColors, loteriaColColors, loteriaStripeColors,
    loteriaFigureImagesRef, loteriaImagesLoaded, drawLoteriaBoard, overlayShadow, skipBackground,
    loteriaBoardNumberingOverrides,
    loteriaBoardDisplayNums,
    overlayShadowOffsetX, overlayShadowOffsetY,
    drawOverlayOutline,
  ]);

  // --- DRAWING HELPERS ---
  const drawMarkupElement = (ctx, el, fs = 1) => {
    ctx.save();
    ctx.globalAlpha = (el.opacity || 100) / 100;
    ctx.strokeStyle = el.color || '#000000';
    ctx.fillStyle = el.color || '#000000';
    // Thickness must scale with `fs` (fullscreen scale) — same way point
    // coordinates do — otherwise strokes that look right at HD/SD render look
    // proportionally thinner in fullscreen because the canvas grew but the
    // stroke didn't.
    ctx.lineWidth = ((el.thickness || 3) * fs) / scale;
    const isSquare = el.tipStyle === 'square';
    ctx.lineCap = isSquare ? 'square' : 'round';
    ctx.lineJoin = isSquare ? 'miter' : 'round';

    if (el.type === 'freehand' && el.points && el.points.length >= 1) {
      if (el.points.length <= 2) {
        // Single click = dot (or auto-mark dot).
        // FS boost: in fullscreen mode, expand auto-marks an extra 1.5x relative
        // to the cells. Mathematically the mark already scales with fsScale so
        // it stays proportional, but viewers prefer marks that visually "pop"
        // more in fullscreen / streaming mode. The boost only applies to
        // auto-marks (autoMarkSize set) — user-drawn dots stay 1:1.
        const pt = el.points[0];
        const fsBoost = (el.autoMarkSize && fs > 1) ? 1.5 : 1;
        const autoSize = el.autoMarkSize ? (el.autoMarkSize * fs * fsBoost) / scale : 0;
        const radius = autoSize > 0 ? autoSize : Math.max(((el.thickness || 3) * fs) / scale / 2, (2 * fs) / scale);
        const cx = (pt.x * fs) / scale;
        const cy = (pt.y * fs) / scale;
        ctx.beginPath();
        if (isSquare) {
          ctx.rect(cx - radius, cy - radius, radius * 2, radius * 2);
        } else {
          ctx.arc(cx, cy, radius, 0, Math.PI * 2);
        }
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo((el.points[0].x * fs) / scale, (el.points[0].y * fs) / scale);
        for (let i = 1; i < el.points.length; i++) {
          ctx.lineTo((el.points[i].x * fs) / scale, (el.points[i].y * fs) / scale);
        }
        ctx.stroke();
      }
    } else if (['line', 'arrow', 'double-arrow'].includes(el.type) && el.startPoint && el.endPoint) {
      const sx = (el.startPoint.x * fs) / scale, sy = (el.startPoint.y * fs) / scale;
      const ex = (el.endPoint.x * fs) / scale, ey = (el.endPoint.y * fs) / scale;
      const dist = Math.sqrt((ex - sx) ** 2 + (ey - sy) ** 2);
      if (dist < 3 / scale) {
        const radius = Math.max(((el.thickness || 3) * fs) / scale / 2, (2 * fs) / scale);
        ctx.beginPath();
        if (isSquare) {
          ctx.rect(sx - radius, sy - radius, radius * 2, radius * 2);
        } else {
          ctx.arc(sx, sy, radius, 0, Math.PI * 2);
        }
        ctx.fill();
      } else {
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.stroke();
        const headSize = (Math.max(10, (el.thickness || 3) * 3) * fs) / scale;
        if (el.type === 'arrow' || el.type === 'double-arrow') {
          drawArrowhead(ctx, sx, sy, ex, ey, headSize);
        }
        if (el.type === 'double-arrow') {
          drawArrowhead(ctx, ex, ey, sx, sy, headSize);
        }
      }
    } else if ((el.type === 'circle' || el.type === 'square') && el.startPoint && el.endPoint) {      // Stroke-only shapes — fully transparent center, see-through to whatever
      // is behind the canvas (board cells, background image, overlays).
      const sx = (el.startPoint.x * fs) / scale, sy = (el.startPoint.y * fs) / scale;
      const ex = (el.endPoint.x * fs) / scale, ey = (el.endPoint.y * fs) / scale;
      const minX = Math.min(sx, ex), maxX = Math.max(sx, ex);
      const minY = Math.min(sy, ey), maxY = Math.max(sy, ey);
      const w = maxX - minX, h = maxY - minY;
      if (w < 2 / scale && h < 2 / scale) {
        // Degenerate (click without drag): draw a tiny outlined dot
        ctx.beginPath();
        ctx.arc(sx, sy, Math.max(((el.thickness || 3) * fs) / scale, (2 * fs) / scale), 0, Math.PI * 2);
        ctx.stroke();
      } else if (el.type === 'square') {
        ctx.beginPath();
        ctx.rect(minX, minY, w, h);
        ctx.stroke();
      } else {
        // Ellipse fit inside the dragged bounding box so the user can draw
        // both perfect circles AND ovals just by varying drag aspect ratio.
        const cx = minX + w / 2, cy = minY + h / 2;
        const rx = Math.max(w / 2, 1), ry = Math.max(h / 2, 1);
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        ctx.stroke();
      }
    } else if (el.type === 'stamp' && el.startPoint && el.endPoint && markupStampImage) {
      // Image stamp — drawn from the global `markupStampImage` data URL.
      // The image is cached in imageCache; if not loaded yet, draw a
      // placeholder rect so the user still sees where the stamp landed.
      const sx = (el.startPoint.x * fs) / scale, sy = (el.startPoint.y * fs) / scale;
      const ex = (el.endPoint.x * fs) / scale, ey = (el.endPoint.y * fs) / scale;
      const minX = Math.min(sx, ex), maxX = Math.max(sx, ex);
      const minY = Math.min(sy, ey), maxY = Math.max(sy, ey);
      const w = maxX - minX, h = maxY - minY;
      const stampImg = imageCache.current.get(markupStampImage);
      if (stampImg && stampImg.complete && stampImg.naturalWidth > 0) {
        ctx.drawImage(stampImg, minX, minY, w, h);
      } else {
        // Placeholder while the image loads
        ctx.save();
        ctx.fillStyle = 'rgba(255, 193, 7, 0.25)';
        ctx.strokeStyle = 'rgba(255, 193, 7, 0.9)';
        ctx.lineWidth = 2 / scale;
        ctx.setLineDash([6 / scale, 4 / scale]);
        ctx.fillRect(minX, minY, w, h);
        ctx.strokeRect(minX, minY, w, h);
        ctx.restore();
      }
    }
    ctx.restore();
  };

  const drawArrowhead = (ctx, fromX, fromY, toX, toY, size) => {
    const angle = Math.atan2(toY - fromY, toX - fromX);
    ctx.beginPath();
    ctx.moveTo(toX, toY);
    ctx.lineTo(toX - size * Math.cos(angle - Math.PI / 6), toY - size * Math.sin(angle - Math.PI / 6));
    ctx.lineTo(toX - size * Math.cos(angle + Math.PI / 6), toY - size * Math.sin(angle + Math.PI / 6));
    ctx.closePath();
    ctx.fill();
  };

  const drawBingoBoard = (ctx, board, x, y, boardSize, cellSize, style, borderColor, headerBgColor, headerTxtColor, cellBgColor, txtColor, font, freeText, freeColor, freeTxtColor, isTransparent, hdrOpacity, cellOp, freeOp, keepHeader, keepFreeSpace) => {
    const letters = ["B", "I", "N", "G", "O"];
    const borderWidth = cellSize * 0.04;
    const cornerRadius = style === "soft" ? cellSize * 0.15 : style === "pill" ? cellSize * 0.4 : 0;

    // Board background/border — draw if not transparent, or if transparent but keeping header
    if (!isTransparent) {
      ctx.fillStyle = borderColor;
      if (style === "shadowed") {
        ctx.fillStyle = "rgba(0,0,0,0.2)";
        drawRoundedRect(ctx, x + 3, y + 3, boardSize, cellSize * 6, cornerRadius);
        ctx.fill();
        ctx.fillStyle = borderColor;
      }
      drawRoundedRect(ctx, x, y, boardSize, cellSize * 6, cornerRadius);
      ctx.fill();
    } else if (keepHeader) {
      // Transparent cells but draw border/fill for header row only
      ctx.fillStyle = borderColor;
      drawRoundedRect(ctx, x, y, boardSize, cellSize, cornerRadius);
      ctx.fill();
    }

    // Header row
    letters.forEach((letter, i) => {
      const cx = x + i * cellSize, cy = y;
      if (!isTransparent || keepHeader) {
        ctx.save();
        ctx.globalAlpha = (hdrOpacity ?? 100) / 100;
        ctx.fillStyle = headerBgColor;
        drawCell(ctx, cx, cy, cellSize, cellSize, style, cornerRadius, borderWidth);
        ctx.fill();
        ctx.restore();
      }
      if (!isTransparent || keepHeader) {
        ctx.strokeStyle = borderColor; ctx.lineWidth = borderWidth;
        if (style === "dotted") ctx.setLineDash([4, 4]);
        else if (style === "double") ctx.lineWidth = borderWidth * 2;
        else ctx.setLineDash([]);
        drawCell(ctx, cx, cy, cellSize, cellSize, style, cornerRadius, borderWidth);
        ctx.stroke(); ctx.setLineDash([]);
      }
      if (!isTransparent || keepHeader) {
        ctx.fillStyle = headerTxtColor;
        const fs = calculateFitFontSize(letter, cellSize * 0.8, cellSize * 0.8, ctx, font);
        ctx.font = `bold ${fs}px ${font}`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillText(letter, cx + cellSize / 2, cy + cellSize / 2);
      }
    });

    for (let row = 0; row < 5; row++) {
      for (let col = 0; col < 5; col++) {
        const cx = x + col * cellSize, cy = y + cellSize + row * cellSize;
        const value = board[row][col];
        const isFree = value === "FREE";
        const showCell = !isTransparent || (isFree && keepFreeSpace);
        if (showCell) {
          ctx.save();
          ctx.globalAlpha = (isFree ? (freeOp ?? 100) : (cellOp ?? 100)) / 100;
          ctx.fillStyle = isFree ? freeColor : cellBgColor;
          drawCell(ctx, cx, cy, cellSize, cellSize, style, cornerRadius, borderWidth);
          ctx.fill();
          ctx.restore();
        }
        if (showCell || !isTransparent) {
          ctx.strokeStyle = borderColor; ctx.lineWidth = borderWidth;
          if (style === "dotted") ctx.setLineDash([4, 4]);
          else if (style === "double") ctx.lineWidth = borderWidth * 2;
          else ctx.setLineDash([]);
          drawCell(ctx, cx, cy, cellSize, cellSize, style, cornerRadius, borderWidth);
          ctx.stroke(); ctx.setLineDash([]);
        }
        if (isFree && isTransparent && keepFreeSpace) {
          // Draw border around free space when keeping it visible
          ctx.strokeStyle = borderColor; ctx.lineWidth = borderWidth;
          ctx.setLineDash([]);
          drawCell(ctx, cx, cy, cellSize, cellSize, style, cornerRadius, borderWidth);
          ctx.stroke();
        }
        ctx.fillStyle = isFree ? freeTxtColor : txtColor;
        const text = isFree ? freeText : String(value);
        const fs = calculateFitFontSize(text, cellSize * 0.75, cellSize * 0.6, ctx, font);
        ctx.font = `bold ${fs}px ${font}`;
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        if (isFree && freeSpaceTextEffect && freeSpaceTextEffect !== 'none') {
          drawTextWithEffect(ctx, text, cx + cellSize / 2, cy + cellSize / 2, freeSpaceTextEffect, (freeSpaceTextThickness || 2) * (fs / 20), ctx.fillStyle);
        } else {
          ctx.fillText(text, cx + cellSize / 2, cy + cellSize / 2);
        }
      }
    }
  };

  const drawCell = (ctx, x, y, width, height, style, radius, borderWidth) => {
    ctx.beginPath();
    if (style === "pill") {
      ctx.ellipse(x + width / 2, y + height / 2, width / 2 - borderWidth, height / 2 - borderWidth, 0, 0, Math.PI * 2);
    } else {
      drawRoundedRect(ctx, x, y, width, height, radius);
    }
  };

  const drawRoundedRect = (ctx, x, y, width, height, radius) => {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  };

  const WATERMARK_FONT_MAP = {
    outfit: "'Outfit', sans-serif",
    anton: "'Anton', sans-serif",
    "bebas-neue": "'Bebas Neue', sans-serif",
    "playfair-display": "'Playfair Display', serif",
    "dancing-script": "'Dancing Script', cursive",
    "permanent-marker": "'Permanent Marker', cursive",
    "press-start": "'Press Start 2P', monospace",
    "special-elite": "'Special Elite', monospace",
    "lobster": "'Lobster', cursive",
    "pacifico": "'Pacifico', cursive",
    "oswald": "'Oswald', sans-serif",
    "righteous": "'Righteous', sans-serif",
    "archivo-black": "'Archivo Black', sans-serif",
    "bungee": "'Bungee', sans-serif",
    "rubik-mono-one": "'Rubik Mono One', monospace",
    "fredoka": "'Fredoka', sans-serif",
    "lilita": "'Lilita One', sans-serif",
    "shrikhand": "'Shrikhand', serif",
    "borsok": "'Borsok', sans-serif",
    "spicy-rice": "'Spicy Rice', serif",
    "just-another-hand": "'Just Another Hand', cursive",
    "londrina-solid": "'Londrina Solid', sans-serif",
    "maiden-orange": "'Maiden Orange', serif",
    "mouse-memoirs": "'Mouse Memoirs', sans-serif",
    "patrick-hand": "'Patrick Hand', cursive",
    "rum-raisin": "'Rum Raisin', sans-serif",
    "skranji": "'Skranji', sans-serif",
    "liberator": "'Liberator', 'Impact', sans-serif",
    "vincent": "'Vincent', 'Playfair Display', serif",
    "chalkboard-se": "'Chalkboard SE', 'Comic Sans MS', cursive",
    "chick": "'Chick', 'Patrick Hand', cursive",
    "cinecaption": "'Cinecaption', 'Special Elite', monospace",
  };

  const drawWatermark = (ctx, text, opacity, color, wmFont, width, height) => {
    ctx.save();
    ctx.globalAlpha = opacity / 100;
    ctx.fillStyle = color || "#000000";
    const wmFamily = WATERMARK_FONT_MAP[wmFont] || WATERMARK_FONT_MAP['outfit'];
    ctx.font = `bold ${height * 0.1}px ${wmFamily}`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.translate(width / 2, height / 2);
    ctx.rotate(-Math.PI / 4);
    for (let i = -2; i <= 2; i++) ctx.fillText(text, 0, i * height * 0.25);
    ctx.restore();
  };

  // --- MOUSE HANDLING ---
  const getMousePos = useCallback((e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    // Convert screen coords to normal display coords (undoing fullscreen scale)
    const rawX = ((e.clientX - rect.left) / rect.width) * displayWidth;
    const rawY = ((e.clientY - rect.top) / rect.height) * displayHeight;
    return { x: rawX / fsScale, y: rawY / fsScale };
  }, [displayWidth, displayHeight, fsScale]);

  const hitTestOverlay = useCallback((pos) => {
    for (let i = overlayImages.length - 1; i >= 0; i--) {
      const o = overlayImages[i];
      const hs = 14;
      if (pos.x >= o.x + o.width - hs && pos.x <= o.x + o.width + hs &&
          pos.y >= o.y + o.height - hs && pos.y <= o.y + o.height + hs) return { type: 'resize', id: o.id };
      if (pos.x >= o.x && pos.x <= o.x + o.width &&
          pos.y >= o.y && pos.y <= o.y + o.height) return { type: 'move', id: o.id };
    }
    return null;
  }, [overlayImages]);

  const hitTestBoard = useCallback((pos) => {
    // Check BINGO boards
    for (let i = bingoBoards.length - 1; i >= 0; i--) {
      const b = bingoBoards[i];
      const th = (b.width / 5) * 6;
      const hs = 14;
      if (pos.x >= b.x + b.width - hs && pos.x <= b.x + b.width + hs &&
          pos.y >= b.y + th - hs && pos.y <= b.y + th + hs) return { type: 'resize', id: b.id, boardType: 'bingo' };
      if (pos.x >= b.x && pos.x <= b.x + b.width &&
          pos.y >= b.y && pos.y <= b.y + th) return { type: 'move', id: b.id, boardType: 'bingo' };
    }
    // Check Loteria boards
    if (loteriaBoards) {
      for (let i = loteriaBoards.length - 1; i >= 0; i--) {
        const b = loteriaBoards[i];
        const bh = b.width * LOTERIA_CELL_RATIO; // rectangular boards
        const hs = 14;
        if (pos.x >= b.x + b.width - hs && pos.x <= b.x + b.width + hs &&
            pos.y >= b.y + bh - hs && pos.y <= b.y + bh + hs) return { type: 'resize', id: b.id, boardType: 'loteria' };
        if (pos.x >= b.x && pos.x <= b.x + b.width &&
            pos.y >= b.y && pos.y <= b.y + bh) return { type: 'move', id: b.id, boardType: 'loteria' };
      }
    }
    return null;
  }, [bingoBoards, loteriaBoards]);

  const hitTestEmoji = useCallback((pos) => {
    if (!emojis) return null;
    for (let i = emojis.length - 1; i >= 0; i--) {
      const em = emojis[i];
      const half = em.size / 2 + 5;
      if (pos.x >= em.x - half && pos.x <= em.x + half &&
          pos.y >= em.y - half && pos.y <= em.y + half) return { type: 'move', id: em.id };
    }
    return null;
  }, [emojis]);

  // Hit-test markup elements (top-most first). Returns the matched element
  // descriptor or null. Also detects clicks on the 4 corner / 4 edge resize
  // handles of the currently-selected circle/square so users can stretch
  // shapes. Used by the unified tool flow: any active markup tool can
  // select + move + resize existing markup.
  const hitTestMarkup = useCallback((pos) => {
    if (!markupElements || markupElements.length === 0) return null;
    // Resize-handle hit-test on the currently-selected shape (top priority
    // so handles win over the shape body).
    if (selectedMarkupId) {
      const sel = markupElements.find(el => el.id === selectedMarkupId);
      if (sel && (sel.type === 'circle' || sel.type === 'square' || sel.type === 'stamp') && sel.startPoint && sel.endPoint) {
        const minX = Math.min(sel.startPoint.x, sel.endPoint.x);
        const maxX = Math.max(sel.startPoint.x, sel.endPoint.x);
        const minY = Math.min(sel.startPoint.y, sel.endPoint.y);
        const maxY = Math.max(sel.startPoint.y, sel.endPoint.y);
        const midX = (minX + maxX) / 2, midY = (minY + maxY) / 2;
        const HANDLE_R = 14 / (scale || 1); // generous tolerance in canvas px
        const handles = [
          { id: 'nw', x: minX, y: minY }, { id: 'n',  x: midX, y: minY }, { id: 'ne', x: maxX, y: minY },
          { id: 'w',  x: minX, y: midY },                                  { id: 'e',  x: maxX, y: midY },
          { id: 'sw', x: minX, y: maxY }, { id: 's',  x: midX, y: maxY }, { id: 'se', x: maxX, y: maxY },
        ];
        for (const h of handles) {
          if (Math.hypot(pos.x - h.x, pos.y - h.y) < HANDLE_R) {
            return { id: sel.id, type: sel.type, handle: h.id };
          }
        }
      }
    }
    // Body hit-test (top-most first)
    for (let i = markupElements.length - 1; i >= 0; i--) {
      const el = markupElements[i];
      const tol = Math.max(8, (el.thickness || 3) * 1.5);
      // Freehand: any point within tol of the cursor
      if (el.points && el.points.length > 0) {
        const hit = el.points.some(p => Math.hypot(p.x - pos.x, p.y - pos.y) < tol);
        if (hit) return { id: el.id, type: el.type, handle: 'move' };
        continue;
      }
      if (!el.startPoint || !el.endPoint) continue;
      const { startPoint: s, endPoint: e } = el;
      if (el.type === 'circle' || el.type === 'square' || el.type === 'stamp') {
        const minX = Math.min(s.x, e.x), maxX = Math.max(s.x, e.x);
        const minY = Math.min(s.y, e.y), maxY = Math.max(s.y, e.y);
        if (pos.x >= minX - tol && pos.x <= maxX + tol &&
            pos.y >= minY - tol && pos.y <= maxY + tol) {
          return { id: el.id, type: el.type, handle: 'move' };
        }
        continue;
      }
      // Line / arrow / double-arrow — distance from pos to the segment
      const dx = e.x - s.x, dy = e.y - s.y;
      const lenSq = dx * dx + dy * dy;
      let t = 0;
      if (lenSq > 0) {
        t = ((pos.x - s.x) * dx + (pos.y - s.y) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
      }
      const cx = s.x + t * dx, cy = s.y + t * dy;
      if (Math.hypot(pos.x - cx, pos.y - cy) < tol) return { id: el.id, type: el.type, handle: 'move' };
    }
    return null;
  }, [markupElements, selectedMarkupId, scale]);

  const handleMouseDown = useCallback((e) => {
    const pos = getMousePos(e);

    // Fullscreen "presenter" lock — Owner accounts only. When an owner is
    // in fullscreen we want a clean, immovable presenter view (no accidental
    // drags, no selection outlines). Subscribers/Demo users keep the legacy
    // behavior where fullscreen still allows drag/resize. Markup tools and
    // hammer mode still pass through for owners.
    if (bgVideoIsOwner && isFullscreen && !activeMarkupTool && !hammerMode) {
      // Clear any lingering selection state so no outline is drawn either.
      if (setSelectedOverlayId) setSelectedOverlayId(null);
      if (setSelectedBoardId) setSelectedBoardId(null);
      if (setSelectedEmojiId) setSelectedEmojiId(null);
      if (setSelectedMarkupId) setSelectedMarkupId(null);
      return;
    }

    // Hammer Mode — intercept BEFORE all other handlers. If the click lands
    // on a board's footprint, fire onHammerStrike with the boardId + screen
    // coords (for the hammer flash visual). Falls through to normal handling
    // only if no board was hit.
    if (hammerMode && onHammerStrike) {
      const allBoards = [
        ...(Array.isArray(bingoBoards) ? bingoBoards.map(b => ({
          ...b, h: ((b.width || 0) / 5) * 6, // BINGO board: 6 rows * cellSize
        })) : []),
        ...(Array.isArray(loteriaBoards) ? loteriaBoards.map(b => ({
          ...b, h: b.height || ((b.width || 0) * LOTERIA_CELL_RATIO),
        })) : []),
      ];
      for (let i = allBoards.length - 1; i >= 0; i--) {
        const b = allBoards[i];
        if (pos.x >= b.x && pos.x <= b.x + b.width && pos.y >= b.y && pos.y <= b.y + b.h) {
          // Convert to screen coords (canvas element + bounding rect)
          const rect = e.currentTarget.getBoundingClientRect();
          const screenX = e.clientX - rect.left;
          const screenY = e.clientY - rect.top;
          // Convert the board footprint (in canvas units) to the same screen
          // pixel space — so the Tiger Claw scratch can rake corner-to-corner
          // across the actual visible board, not just near the click point.
          const sx = fsScale * (rect.width / displayWidth);
          const sy = fsScale * (rect.height / displayHeight);
          const boardRect = {
            left: b.x * sx,
            top: b.y * sy,
            width: b.width * sx,
            height: b.h * sy,
          };
          onHammerStrike(b.id, pos, { x: screenX, y: screenY, boardRect });
          return;
        }
      }
      // No board hit — swallow the click so other tools don't trigger in hammer mode.
      return;
    }

    // Eraser mode
    if (activeMarkupTool === 'eraser') {
      setActiveDrawing({ type: 'eraser', points: [pos] });
      // Erase any markup under this point immediately
      if (onEraseMarkup) onEraseMarkup(pos);
      return;
    }

    // Markup drawing mode
    if (activeMarkupTool) {
      // Hit-test existing markup elements FIRST. If the click lands on an
      // existing element, select+drag it. If on empty canvas, fall through
      // to start a new drawing. This unifies the previous "Select" tool
      // into every markup tool: users can move/resize existing markup while
      // any markup tool is active, and markup is locked when no tool active.
      //
      // EXCEPTION: when the active tool is a SHAPE creation tool (circle /
      // square), an existing image stamp's BODY does NOT intercept clicks.
      // Stamps typically cover entire boards, so without this exception the
      // user could never draw a circle on top of a stamped board (every
      // click would land on the stamp and start dragging it instead).
      // Resize handles on the selected stamp still work — only body-drags
      // are suppressed in this case.
      const markupHit = hitTestMarkup(pos);
      const isShapeCreation = activeMarkupTool === 'circle' || activeMarkupTool === 'square';
      const stampBlocksShape = markupHit && markupHit.type === 'stamp' && markupHit.handle === 'move' && isShapeCreation;
      if (markupHit && !stampBlocksShape) {
        if (setSelectedMarkupId) setSelectedMarkupId(markupHit.id);
        markupDragOriginRef.current = {
          id: markupHit.id,
          handle: markupHit.handle || null, // 'move' | 'nw'|'ne'|'sw'|'se'|'n'|'s'|'e'|'w'|'start'|'end'
          lastPos: pos,
        };
        if (saveToHistory) saveToHistory(); // single history checkpoint per drag
        return;
      }
      // Clicked empty canvas (or a stamp that was passed-through by the
      // shape-creation exception above) — clear any current selection.
      if (setSelectedMarkupId) setSelectedMarkupId(null);

      // Stamp tool — drop an image-mark on the clicked board. First click
      // uses a default size centered on the click point; subsequent clicks
      // auto-place at the same relative offset + size captured into the
      // template. Requires an uploaded stamp image AND a board hit.
      if (activeMarkupTool === 'stamp') {
        if (!markupStampImage) return; // upload first
        // Find which board was clicked
        const allBoards = [
          ...(Array.isArray(bingoBoards) ? bingoBoards.map(b => ({
            ...b, h: ((b.width || 0) / 5) * 6, kind: 'bingo',
          })) : []),
          ...(Array.isArray(loteriaBoards) ? loteriaBoards.map(b => ({
            ...b, h: b.height || ((b.width || 0) * LOTERIA_CELL_RATIO), kind: 'loteria',
          })) : []),
        ];
        let hitBoard = null;
        for (let i = allBoards.length - 1; i >= 0; i--) {
          const b = allBoards[i];
          if (pos.x >= b.x && pos.x <= b.x + b.width && pos.y >= b.y && pos.y <= b.y + b.h) {
            hitBoard = b;
            break;
          }
        }
        if (!hitBoard) return;

        // Determine stamp rect — use template if set, else default centered.
        let rectX, rectY, rectW, rectH;
        if (markupStampTemplate && typeof markupStampTemplate.width === 'number') {
          rectX = hitBoard.x + (markupStampTemplate.relX || 0);
          rectY = hitBoard.y + (markupStampTemplate.relY || 0);
          rectW = markupStampTemplate.width;
          rectH = markupStampTemplate.height;
        } else {
          const DEFAULT_SIZE = Math.min(hitBoard.width || 200, 160);
          rectW = DEFAULT_SIZE;
          rectH = DEFAULT_SIZE;
          rectX = pos.x - rectW / 2;
          rectY = pos.y - rectH / 2;
          // Capture template (relative to the board) so subsequent clicks
          // on other boards auto-place at the same relative position+size.
          if (setMarkupStampTemplate) {
            setMarkupStampTemplate({
              relX: rectX - hitBoard.x,
              relY: rectY - hitBoard.y,
              width: rectW,
              height: rectH,
            });
          }
        }

        const newStamp = {
          id: `markup-stamp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          type: 'stamp',
          boardId: hitBoard.id,
          startPoint: { x: rectX, y: rectY },
          endPoint:   { x: rectX + rectW, y: rectY + rectH },
          opacity: markupOpacity,
        };
        if (onMarkupComplete) onMarkupComplete(newStamp);
        if (setSelectedMarkupId) setSelectedMarkupId(newStamp.id);
        return;
      }

      if (activeMarkupTool === 'freehand') {
        setActiveDrawing({ type: 'freehand', points: [pos], color: markupColor, thickness: markupThickness, tipStyle: markupTipStyle, opacity: markupOpacity });
      } else if (activeMarkupTool !== 'eraser') {
        setActiveDrawing({ type: activeMarkupTool, startPoint: pos, endPoint: pos, color: markupColor, thickness: markupThickness, tipStyle: markupTipStyle, opacity: markupOpacity });
      }
      return;
    }

    // Normal select/move mode — test emojis FIRST (they render on top)
    const emojiHit = hitTestEmoji(pos);
    if (emojiHit) {
      setSelectedEmojiId(emojiHit.id); setSelectedOverlayId(null); setSelectedBoardId(null);
      setDragTarget({ type: 'emoji', id: emojiHit.id });
      const em = emojis.find(e => e.id === emojiHit.id);
      setIsDragging(true); setDragOffset({ x: pos.x - em.x, y: pos.y - em.y });
      return;
    }

    // Hit-test extra texts FIRST (top-most layer, drawn last in render).
    // We iterate from end of array (rendered on top) to start (rendered first).
    if (Array.isArray(extraTemplateTexts) && extraTemplateTexts.length > 0 && onExtraTextMove) {
      for (let i = extraTemplateTexts.length - 1; i >= 0; i--) {
        const t = extraTemplateTexts[i];
        if (!t || !t.text || !t.text.trim()) continue;
        const sz = t.size || 64;
        const fam = WATERMARK_FONT_MAP[t.font] || WATERMARK_FONT_MAP['outfit'];
        const probe = document.createElement('canvas');
        const pp = probe.getContext('2d');
        pp.font = `bold ${sz}px ${fam}`;
        const tw = pp.measureText(t.text).width;
        const th = sz * 1.2;
        const dCx = displayWidth / fsScale / 2;
        const dCy = sz * 0.25;
        const cM = (t.x != null) ? t.x : dCx;
        const cT = (t.y != null) ? t.y : dCy;
        if (pos.x >= cM - tw / 2 - 8 && pos.x <= cM + tw / 2 + 8 &&
            pos.y >= cT - 8 && pos.y <= cT + th + 8) {
          setSelectedOverlayId(null); setSelectedBoardId(null); setSelectedEmojiId(null);
          setDragTarget({ type: 'extraText', id: t.id });
          setIsDragging(true);
          setDragOffset({ x: pos.x - cM, y: pos.y - cT });
          return;
        }
      }
    }

    // Test Template Text bounding box (top-most layer aside from emojis).
    // Working in DISPLAY space (matching pos coords from getMousePos).
    if (templateText && templateText.trim() !== '' && onTemplateTextMove) {
      const ttSize = templateTextSize || 64;  // display-space size
      const ttFamily = WATERMARK_FONT_MAP[templateTextFont] || WATERMARK_FONT_MAP['outfit'];
      const probeCanvas = document.createElement('canvas');
      const pctx = probeCanvas.getContext('2d');
      pctx.font = `bold ${ttSize}px ${ttFamily}`;
      const m = pctx.measureText(templateText);
      const textW = m.width;
      const textH = ttSize * 1.2;
      // Default position in display space
      const defaultCx = displayWidth / fsScale / 2;
      const defaultCy = ttSize * 0.25;
      const cxMid = (templateTextX != null) ? templateTextX : defaultCx;
      const cyTop = (templateTextY != null) ? templateTextY : defaultCy;
      const x0 = cxMid - textW / 2 - 8;
      const x1 = cxMid + textW / 2 + 8;
      const y0 = cyTop - 8;
      const y1 = cyTop + textH + 8;
      if (pos.x >= x0 && pos.x <= x1 && pos.y >= y0 && pos.y <= y1) {
        setSelectedOverlayId(null); setSelectedBoardId(null); setSelectedEmojiId(null);
        setDragTarget({ type: 'templateText' });
        setIsDragging(true);
        setDragOffset({ x: pos.x - cxMid, y: pos.y - cyTop });
        return;
      }
    }

    const overlayHit = hitTestOverlay(pos);
    if (overlayHit) {
      setSelectedOverlayId(overlayHit.id); setSelectedBoardId(null); setSelectedEmojiId(null);
      setDragTarget({ type: 'overlay', id: overlayHit.id });
      if (overlayHit.type === 'resize') { setIsResizing(true); }
      else {
        const o = overlayImages.find(o => o.id === overlayHit.id);
        setIsDragging(true); setDragOffset({ x: pos.x - o.x, y: pos.y - o.y });
      }
      return;
    }

    // Prize Dots — drag the whole group by clicking any dot.
    // pos is in normal-display space; the dots are drawn in canvas-native
    // space (using canvasWidth/canvasHeight), so we convert pos → canvas-native
    // for the hit-test using the same factor the render uses.
    if (prizeDots && prizeDots.enabled && prizeDots.count > 0 && setPrizeDots) {
      const pcx = (pos.x * fsScale) / scale;
      const pcy = (pos.y * fsScale) / scale;
      const r = prizeDots.dotSize || 60;
      const gap = prizeDots.spacing || 40;
      const totalW = prizeDots.count * (r * 2) + Math.max(0, prizeDots.count - 1) * gap;
      const groupX = (prizeDots.x != null ? prizeDots.x : (canvasWidth / 2 - totalW / 2));
      const groupY = (prizeDots.y != null ? prizeDots.y : (canvasHeight * 0.88));
      for (let i = 0; i < prizeDots.count; i++) {
        const dotX = groupX + r + i * (r * 2 + gap);
        const dx = pcx - dotX;
        const dy = pcy - groupY;
        if (dx * dx + dy * dy <= r * r) {
          setSelectedOverlayId(null); setSelectedBoardId(null); setSelectedEmojiId(null);
          setDragTarget({ type: 'prizeDots' });
          setIsDragging(true);
          setDragOffset({ x: pcx - groupX, y: pcy - groupY });
          return;
        }
      }
    }

    const boardHit = hitTestBoard(pos);
    if (boardHit) {
      setSelectedBoardId(boardHit.id); setSelectedOverlayId(null); setSelectedEmojiId(null);
      
      // Loteria drag mode based on sidebar move mode setting — now works
      // for BINGO boards too. Same state (`loteriaMoveMode`) is reused for
      // both game modes so users get a single "Move Boards" toggle.
      if (boardHit.type !== 'resize') {
        if (loteriaMoveMode === 'vertical') {
          loteriaDragModeRef.current = 'column';
          setLoteriaDragMode('column');
        } else if (loteriaMoveMode === 'horizontal') {
          loteriaDragModeRef.current = 'row';
          setLoteriaDragMode('row');
        } else {
          loteriaDragModeRef.current = 'single';
          setLoteriaDragMode('single');
        }
      } else {
        loteriaDragModeRef.current = 'single';
        setLoteriaDragMode('single');
      }
      
      setDragTarget({ type: 'board', id: boardHit.id, boardType: boardHit.boardType });
      didDragRef.current = false;
      if (boardHit.type === 'resize') { setIsResizing(true); }
      else {
        const allBoards = boardHit.boardType === 'loteria' ? (loteriaBoards || []) : bingoBoards;
        const b = allBoards.find(b => b.id === boardHit.id);
        if (b) {
          if (saveToHistory) saveToHistory(); // Save BEFORE drag starts for undo
          setIsDragging(true);
          setDragOffset({ x: pos.x - b.x, y: pos.y - b.y });
          lastDragPosRef.current = { x: pos.x, y: pos.y };
        }
      }
      return;
    }

    setSelectedOverlayId(null); setSelectedBoardId(null); setSelectedEmojiId(null);
  }, [getMousePos, activeMarkupTool, markupColor, markupThickness, markupTipStyle, markupOpacity,
      hitTestOverlay, hitTestBoard, hitTestEmoji, hitTestMarkup, overlayImages, bingoBoards, loteriaBoards, emojis,
      loteriaMoveMode, saveToHistory,
      templateTextEnabled, templateText, templateTextFont, templateTextSize, templateTextX, templateTextY, displayWidth, fsScale, onTemplateTextMove,
      extraTemplateTexts, onExtraTextMove,
      hammerMode, onHammerStrike,
      isFullscreen,
      prizeDots, setPrizeDots, canvasWidth, canvasHeight, fsScale, scale,
      setSelectedOverlayId, setSelectedBoardId, setSelectedEmojiId, setSelectedMarkupId,
      markupStampImage, markupStampTemplate, setMarkupStampTemplate, onMarkupComplete]);

  const handleMouseMove = useCallback((e) => {
    const pos = getMousePos(e);

    // Drag-to-move / resize on a selected markup element (any markup tool active)
    if (markupDragOriginRef.current && activeMarkupTool) {
      const { id, lastPos, handle } = markupDragOriginRef.current;
      const dx = pos.x - lastPos.x;
      const dy = pos.y - lastPos.y;
      if (dx === 0 && dy === 0) return;
      if (handle === 'move') {
        if (moveMarkupElement) moveMarkupElement(id, dx, dy);
      } else if (handle && resizeMarkupElement) {
        // Forward absolute cursor position; resizeMarkupElement applies it
        // to the appropriate corner / edge of the shape's bounding box.
        resizeMarkupElement(id, handle, pos);
      }
      markupDragOriginRef.current.lastPos = pos;
      return;
    }

    // Markup drawing in progress
    if (activeDrawing) {
      if (activeDrawing.type === 'eraser') {
        // Eraser must run synchronously per move so onEraseMarkup can hit
        // each underlying element along the path. It's cheap (no canvas
        // re-render is needed when a markup element is removed elsewhere).
        setActiveDrawing(prev => ({ ...prev, points: [...prev.points, pos] }));
        if (onEraseMarkup) onEraseMarkup(pos);
        return;
      }
      // Freehand / shape — batch updates per animation frame so the
      // canvas only re-renders at 60fps even if mousemove fires 100-200
      // times per second on a high-refresh display.
      if (activeDrawing.type === 'freehand') {
        // Min-distance filter: skip points within ~1 canvas px of the last
        // queued point. Cuts the point array length and keeps strokes
        // crisp without sub-pixel noise.
        const buf = pendingDrawRef.current;
        const lastPt = buf?.points?.length
          ? buf.points[buf.points.length - 1]
          : (activeDrawing.points?.length ? activeDrawing.points[activeDrawing.points.length - 1] : null);
        if (lastPt && Math.hypot(pos.x - lastPt.x, pos.y - lastPt.y) < 1) return;
        if (!buf) pendingDrawRef.current = { points: [pos], endPoint: null };
        else buf.points.push(pos);
      } else {
        // Line / arrow / double-arrow / circle / square — only track the
        // current endpoint. Latest wins.
        if (!pendingDrawRef.current) pendingDrawRef.current = { points: null, endPoint: pos };
        else pendingDrawRef.current.endPoint = pos;
      }
      if (drawRAFRef.current == null) {
        drawRAFRef.current = requestAnimationFrame(() => {
          drawRAFRef.current = null;
          const pending = pendingDrawRef.current;
          pendingDrawRef.current = null;
          if (!pending) return;
          setActiveDrawing(prev => {
            if (!prev) return prev;
            if (pending.points) return { ...prev, points: [...(prev.points || []), ...pending.points] };
            if (pending.endPoint) return { ...prev, endPoint: pending.endPoint };
            return prev;
          });
        });
      }
      return;
    }

    if (!isDragging && !isResizing) return;
    if (!dragTarget) return;

    if (dragTarget.type === 'prizeDots') {
      if (setPrizeDots) {
        // pos is in normal-display space; convert to canvas-native to match
        // the coord space that prizeDots.x/y are stored & rendered in.
        const pcx = (pos.x * fsScale) / scale;
        const pcy = (pos.y * fsScale) / scale;
        setPrizeDots(prev => ({ ...prev, x: pcx - dragOffset.x, y: pcy - dragOffset.y }));
      }
      return;
    }

    if (dragTarget.type === 'overlay') {
      const o = overlayImages.find(o => o.id === dragTarget.id);
      if (!o) return;
      if (isResizing) { updateOverlaySize(dragTarget.id, Math.max(8, pos.x - o.x), Math.max(8, pos.y - o.y)); }
      else { updateOverlayPosition(dragTarget.id, pos.x - dragOffset.x, pos.y - dragOffset.y); }
    } else if (dragTarget.type === 'board') {
      const boards = dragTarget.boardType === 'loteria' ? (loteriaBoards || []) : bingoBoards;
      const b = boards.find(b => b.id === dragTarget.id);
      if (!b) return;
      if (isResizing) {
        updateAllBoardsSize(Math.max(80, pos.x - b.x), Math.max(96, (pos.y - b.y) / 1.2));
        setSnapGuides([]);
      } else if (loteriaDragModeRef.current !== 'single') {
        // Group move: column / row — for BOTH bingo and loteria boards.
        // With snap guides.
        const dx = pos.x - lastDragPosRef.current.x;
        const dy = pos.y - lastDragPosRef.current.y;
        didDragRef.current = true;

        // Compute where the dragged board WOULD end up
        const rawX = b.x + dx;
        const rawY = b.y + dy;
        const { guides, snappedX, snappedY } = computeSnapGuides(dragTarget.id, rawX, rawY, dragTarget.boardType);
        setSnapGuides(guides);
        const snapDx = snappedX - b.x;
        const snapDy = snappedY - b.y;
        lastDragPosRef.current = { x: pos.x + (snappedX - rawX), y: pos.y + (snappedY - rawY) };

        const isBingo = dragTarget.boardType !== 'loteria';
        if (loteriaDragModeRef.current === 'column') {
          if (isBingo) { moveBingoColumn && moveBingoColumn(dragTarget.id, snapDx, snapDy); }
          else { moveLoteriaColumn(dragTarget.id, snapDx, snapDy); }
        } else {
          if (isBingo) { moveBingoRow && moveBingoRow(dragTarget.id, snapDx, snapDy); }
          else { moveLoteriaRow(dragTarget.id, snapDx, snapDy); }
        }
      } else {
        const rawX = pos.x - dragOffset.x, rawY = pos.y - dragOffset.y;
        const { guides, snappedX, snappedY } = computeSnapGuides(dragTarget.id, rawX, rawY, dragTarget.boardType);
        setSnapGuides(guides);
        updateBoardPosition(dragTarget.id, snappedX, snappedY);
        didDragRef.current = true;
      }
    } else if (dragTarget.type === 'emoji') {
      updateEmojiPosition(dragTarget.id, pos.x - dragOffset.x, pos.y - dragOffset.y);
    } else if (dragTarget.type === 'templateText') {
      if (onTemplateTextMove) {
        onTemplateTextMove(pos.x - dragOffset.x, pos.y - dragOffset.y);
      }
    } else if (dragTarget.type === 'extraText') {
      if (onExtraTextMove) {
        onExtraTextMove(dragTarget.id, pos.x - dragOffset.x, pos.y - dragOffset.y);
      }
    }
  }, [activeDrawing, isDragging, isResizing, dragTarget, dragOffset, getMousePos, activeMarkupTool, moveMarkupElement, resizeMarkupElement, onEraseMarkup,
      overlayImages, bingoBoards, loteriaBoards, updateOverlayPosition, updateOverlaySize,
      updateBoardPosition, moveLoteriaColumn, moveLoteriaRow, moveBingoColumn, moveBingoRow, updateAllBoardsSize, updateEmojiPosition, computeSnapGuides, onTemplateTextMove, onExtraTextMove, setPrizeDots, fsScale, scale]);

  const handleMouseUp = useCallback(() => {
    // End markup drag-to-move (Select tool)
    if (markupDragOriginRef.current) {
      markupDragOriginRef.current = null;
      if (saveToHistory) saveToHistory(); // single history entry per drag
      return;
    }
    // Complete markup drawing
    if (activeDrawing) {
      // Flush any pending freehand points / endpoint that the rAF batch
      // hadn't applied yet — guarantees the very end of the stroke isn't
      // missing the last few mousemove positions.
      let drawing = activeDrawing;
      if (pendingDrawRef.current) {
        const p = pendingDrawRef.current;
        if (p.points && p.points.length) drawing = { ...drawing, points: [...(drawing.points || []), ...p.points] };
        else if (p.endPoint) drawing = { ...drawing, endPoint: p.endPoint };
        pendingDrawRef.current = null;
      }
      if (drawRAFRef.current != null) {
        cancelAnimationFrame(drawRAFRef.current);
        drawRAFRef.current = null;
      }
      let finalized = drawing;
      // Auto-square circle / square shapes on release — user drags freely,
      // we normalize the bounding box to a perfect square (using the larger
      // side) centered on the original drag center. Produces perfect circles
      // from the ellipse renderer and perfect squares from the rect renderer.
      if ((drawing.type === 'circle' || drawing.type === 'square')
          && drawing.startPoint && drawing.endPoint) {
        const sP = drawing.startPoint, eP = drawing.endPoint;
        const minX = Math.min(sP.x, eP.x), maxX = Math.max(sP.x, eP.x);
        const minY = Math.min(sP.y, eP.y), maxY = Math.max(sP.y, eP.y);
        const w = maxX - minX, h = maxY - minY;
        const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
        const side = Math.max(w, h);
        if (side > 1) {
          const half = side / 2;
          finalized = {
            ...drawing,
            startPoint: { x: cx - half, y: cy - half },
            endPoint:   { x: cx + half, y: cy + half },
          };
        }
      }
      if (finalized.type !== 'eraser' && onMarkupComplete) onMarkupComplete(finalized);
      setActiveDrawing(null);
      return;
    }
    // Save to history after resize (drag history was saved on mouseDown)
    if (isResizing && saveToHistory) saveToHistory();
    setIsDragging(false); setIsResizing(false); setDragTarget(null);
    setSnapGuides([]);
    didDragRef.current = false;
  }, [activeDrawing, onMarkupComplete, isDragging, isResizing, saveToHistory]);

  // Cursor
  const [cursor, setCursor] = useState('default');
  const mousePos = useRef(null);
  const handleMouseMoveForCursor = useCallback((e) => {
    const pos = getMousePos(e);
    mousePos.current = pos;
    if (activeMarkupTool) { setCursor(activeMarkupTool === 'eraser' ? 'cell' : 'crosshair'); handleMouseMove(e); return; }
    if (!isDragging && !isResizing && !activeDrawing) {
      const pos = getMousePos(e);
      const oh = hitTestOverlay(pos);
      if (oh) { setCursor(oh.type === 'resize' ? 'nwse-resize' : 'move'); }
      else {
        const bh = hitTestBoard(pos);
        if (bh) { setCursor(bh.type === 'resize' ? 'nwse-resize' : 'move'); }
        else {
          const eh = hitTestEmoji(pos);
          setCursor(eh ? 'move' : 'default');
        }
      }
    }
    handleMouseMove(e);
  }, [activeMarkupTool, isDragging, isResizing, activeDrawing, getMousePos,
      hitTestOverlay, hitTestBoard, hitTestEmoji, handleMouseMove]);

  // Convert touch event to mouse-like event with clientX/clientY
  const touchToMouseEvent = useCallback((e) => {
    const touch = e.touches[0] || e.changedTouches[0];
    if (!touch) return null;
    return { clientX: touch.clientX, clientY: touch.clientY };
  }, []);

  // Attach touch listeners with { passive: false } so preventDefault works
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      const fakeEvent = touchToMouseEvent(e);
      if (!fakeEvent) return;
      // Prevent scroll when a markup tool is active
      if (activeMarkupTool) e.preventDefault();
      handleMouseDown(fakeEvent);
    };
    const onTouchMove = (e) => {
      const fakeEvent = touchToMouseEvent(e);
      if (!fakeEvent) return;
      if (activeMarkupTool || activeDrawing || isDragging || isResizing) e.preventDefault();
      handleMouseMoveForCursor(fakeEvent);
    };
    const onTouchEnd = (e) => {
      if (activeMarkupTool || activeDrawing) e.preventDefault();
      handleMouseUp();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: false });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: false });
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [touchToMouseEvent, activeMarkupTool, activeDrawing, isDragging, isResizing,
      handleMouseDown, handleMouseMoveForCursor, handleMouseUp]);

  return (
    <div
      ref={containerRef}
      data-testid="canvas-editor"
      className="flex flex-col items-center"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMoveForCursor}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{ cursor: hammerMode ? 'crosshair' : (activeMarkupTool ? (activeMarkupTool === 'eraser' ? 'cell' : 'crosshair') : (loteriaDragMode === 'column' && isDragging ? 'col-resize' : loteriaDragMode === 'row' && isDragging ? 'row-resize' : cursor)), touchAction: activeMarkupTool || hammerMode ? 'none' : 'auto', position: 'relative' }}
    >
      {/* Animation Background — sits exactly behind the canvas at the
          canvas's rendered size (displayWidth × displayHeight) in BOTH
          fullscreen and regular mode. Owner-only. */}
      {bgVideoIsOwner && callerBgVideoSrc && (
        <video
          data-testid="fullscreen-bg-video"
          key={callerBgVideoSrc}
          src={callerBgVideoSrc}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          disablePictureInPicture
          disableRemotePlayback
          ref={(el) => { if (el) { el.playbackRate = obsMode ? 0.5 : 1; } }}
          style={{
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: obsMode ? 'translateX(-50%) translateZ(0)' : 'translateX(-50%)',
            width: displayWidth,
            height: displayHeight,
            objectFit: callerBgVideoFit || 'cover',
            opacity: (callerBgVideoOpacity || 60) / 100,
            pointerEvents: 'none',
            zIndex: 0,
            willChange: 'transform',
            contain: obsMode ? 'paint' : undefined,
          }}
        />
      )}
      <canvas
        ref={canvasRef}
        width={canvasWidth * superSample}
        height={canvasHeight * superSample}
        style={{ width: displayWidth, height: displayHeight, position: 'relative', zIndex: 1 }}
        className={isFullscreen ? "" : "border-2 border-black bg-white"}
      />
      {/* Animated overlay videos — HTML <video> elements positioned ABOVE
          the canvas at each animated overlay's rect. Loop, muted, autoplay.
          Static overlays continue to be drawn directly on the canvas. */}
      {Array.isArray(overlayImages) && overlayImages.filter(o => o.isAnimated && o.videoSrc).map(ov => {
        // Coordinate conversion: overlay coords are in canvas-native pixels;
        // the canvas is rendered at displayWidth × displayHeight. Multiply by
        // displayWidth/canvasWidth to map back to display pixels.
        const sx = displayWidth / Math.max(1, canvasWidth);
        const sy = displayHeight / Math.max(1, canvasHeight);
        const opacity = typeof ov.opacity === 'number' ? ov.opacity / 100 : 1;
        return (
          <video
            key={ov.id}
            data-testid={`animated-overlay-video-${ov.id}`}
            src={ov.videoSrc}
            autoPlay
            muted
            loop
            playsInline
            style={{
              position: 'absolute',
              left: (ov.x || 0) * sx,
              top: (ov.y || 0) * sy,
              width: (ov.width || 200) * sx,
              height: (ov.height || 200) * sy,
              transform: `rotate(${ov.rotation || 0}deg) scale(${ov.flipH ? -1 : 1}, ${ov.flipV ? -1 : 1})`,
              transformOrigin: 'center',
              opacity,
              pointerEvents: 'none',
              objectFit: 'contain',
              // Subtle CSS crossfade at the seam — masks Sora/Kling's non-perfect loop.
              animation: 'overlayVideoFade 4s ease-in-out infinite',
            }}
          />
        );
      })}
      {/* Hammer / Tiger Claw strike — visual at the click point that
          animates and fades, paired with the canvas board shake + Web
          Audio knock or scratch. */}
      {hammerStrike && !['shamrocks', 'watergun'].includes(hammerStrike.type) && (hammerStrike.type === 'tigerClaw' ? (() => {
        // Position the paw so its claws drag along the board's diagonal —
        // exactly the same axis as the scratch decals. Native asset
        // orientation (claws→heel) is roughly 56° measured from the x-axis
        // in image space; we subtract that so the paw's claw axis aligns
        // with the board's diagonal.
        const br = hammerStrike.boardRect;
        const cx = br ? br.left + br.width / 2 : hammerStrike.x;
        const cy = br ? br.top + br.height / 2 : hammerStrike.y;
        // Render the paw in its NATIVE orientation as supplied by the
        // user (heel on left, claws on right pointing up-right) — no
        // in-code rotation. The paw image fills its container at the
        // intrinsic aspect ratio.
        const pawRotation = 0;
        // Size paw to fit comfortably INSIDE the board — use the board's
        // shorter side so the rotated paw never overflows the board edges.
        // Stationary paw (no big swing) so we can size it a bit larger
        // (70% of short side) for a more prominent visual.
        const boardMinSide = br ? Math.min(br.width, br.height) : 200;
        // Paw fills width to about 60% of the board's shorter side; height
        // scales to preserve the source image's 1024×866 aspect ratio.
        const pawWidth = boardMinSide * 0.6;
        const pawHeight = pawWidth * (866 / 1024);
        const pawSize = pawWidth; // legacy variable name kept for the keyframe CSS vars
        // 3 strike X positions across the board (left/center/right), each
        // exposed as a CSS variable so the keyframe can reposition the
        // paw between strikes. Values are X offsets from board center.
        const strikeXFracs = hammerStrike.strikeXFracs || [0.22, 0.5, 0.78];
        const strikeX0 = br ? (strikeXFracs[0] - 0.5) * br.width : -80;
        const strikeX1 = br ? (strikeXFracs[1] - 0.5) * br.width : 0;
        const strikeX2 = br ? (strikeXFracs[2] - 0.5) * br.width : 80;
        // Y bounds for the rake motion — constrained so the paw stays
        // ENTIRELY INSIDE the board's vertical bounds. The paw center
        // travels from `top edge + pawSize/2` to `bottom edge - pawSize/2`
        // and is shifted slightly upward (rake stays in the upper 2/3 of
        // the board) for the "orientation shifted upward" look.
        const halfPaw = pawHeight * 0.5;
        const boardHalfH = br ? br.height / 2 : 200;
        const rakeTopY = -(boardHalfH - halfPaw);            // paw fully inside, near top
        const rakeBottomY = boardHalfH - halfPaw - pawHeight * 0.2; // bias upward
        return (
          <div
            data-testid="tiger-claw-flash"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: cx,
              top: cy,
              width: pawWidth,
              height: pawHeight,
              ['--paw-angle']: `${pawRotation}deg`,
              ['--strike-x-0']: `${strikeX0}px`,
              ['--strike-x-1']: `${strikeX1}px`,
              ['--strike-x-2']: `${strikeX2}px`,
              ['--rake-top-y']: `${rakeTopY}px`,
              ['--rake-bottom-y']: `${rakeBottomY}px`,
              transform: `translate(-50%, -50%) translate(${strikeX0}px, ${rakeTopY}px) rotate(${pawRotation}deg)`,
              pointerEvents: 'none',
              zIndex: 50,
              animation: 'tigerClawPawSwipe 1500ms linear',
              filter: 'drop-shadow(0 10px 16px rgba(40,15,0,0.7))',
              transformOrigin: 'center',
            }}
          >
            <img
              src="/effects/paw.png"
              alt=""
              draggable={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          </div>
        );
      })() : (
        <div
          data-testid="hammer-flash"
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: hammerStrike.x,
            top: hammerStrike.y,
            transform: 'translate(-30%, -90%)',
            pointerEvents: 'none',
            fontSize: Math.max(48, Math.min(displayWidth || 800, displayHeight || 600) * 0.12),
            lineHeight: 1,
            zIndex: 50,
            animation: 'hammerSwing 1500ms cubic-bezier(.34,1.56,.64,1)',
            filter: 'drop-shadow(0 6px 8px rgba(0,0,0,0.5))',
          }}
        >
          🔨
        </div>
      ))}

      {/* Scratch decals — the user-supplied 4-claw scratch artwork, one per
          strike, layered along the board's diagonal at a small perpendicular
          offset so the 3 strikes read as one heavy rake. The image is
          pre-flipped horizontally so its native top-left→bottom-right
          diagonal matches the board diagonal with minimal rotation. */}
      {Array.isArray(scratchDecals) && scratchDecals.map((d) => {
        const br = d.boardRect;
        let left, top, width, height, angle;
        // The new paw image has claws pointing to the right (~horizontal).
        // For the scratches to align with the claws, we render the scratch
        // artwork at the same horizontal orientation. The scratch image
        // natively has 4 lines running diagonally (top-right → bottom-left
        // after preprocessing); rotating by SCRATCH_NATIVE_TILT brings the
        // lines to horizontal so each line aligns with one claw.
        const SCRATCH_NATIVE_TILT = 30;
        if (br) {
          angle = SCRATCH_NATIVE_TILT;
          // Scale the scratch artwork so its 4 parallel claw lines have
          // the SAME displayed width as the paw's 4 claw tips spread.
          const boardMinSide = Math.min(br.width, br.height);
          width = boardMinSide * 0.26;
          height = width * (742 / 548);
          // Position each of the 3 scratch decals at its own strike X
          // position across the board. Y is biased upward (35% from top)
          // to match the paw's upward-shifted rake motion — the scratches
          // appear where the claws have raked across the upper portion
          // of the board, not at the bottom.
          const strikeXFracs = d.strikeXFracs || [0.22, 0.5, 0.78];
          const xFrac = strikeXFracs[d.strikeIndex != null ? d.strikeIndex : 1];
          left = br.left + xFrac * br.width;
          top = br.top + br.height * 0.42;
        } else {
          width = 80;
          height = 110;
          angle = 0;
          left = d.fallbackX;
          top = d.fallbackY;
        }
        return (
          <img
            key={d.id}
            data-testid="scratch-decal"
            src="/effects/scratches.png"
            alt=""
            draggable={false}
            aria-hidden="true"
            style={{
              position: 'absolute',
              left,
              top,
              width,
              height,
              ['--scratch-angle']: `${angle}deg`,
              transform: `translate(-50%, -50%) rotate(${angle}deg)`,
              pointerEvents: 'none',
              zIndex: 49,
              animation: 'scratchFlash 1500ms ease-out forwards',
              filter: 'drop-shadow(0 3px 4px rgba(0,0,0,0.55))',
              userSelect: 'none',
            }}
          />
        );
      })}

      {/* Lucky Shamrocks — falling 4-leaf clovers strictly contained
          within the clicked board's bounds. Each particle is positioned
          inside an overflow:hidden wrapper sized to the board so even
          if the fall animation overshoots, no shamrock leaks outside. */}
      {Array.isArray(shamrockParticles) && shamrockParticles.length > 0 && (() => {
        // Group particles by boardRect — typically all from one click
        // share the same board, but support multiple just in case.
        const groups = new Map();
        for (const p of shamrockParticles) {
          if (!p.boardRect) continue;
          const key = `${Math.round(p.boardRect.left)}-${Math.round(p.boardRect.top)}-${Math.round(p.boardRect.width)}-${Math.round(p.boardRect.height)}`;
          if (!groups.has(key)) groups.set(key, { br: p.boardRect, parts: [] });
          groups.get(key).parts.push(p);
        }
        const out = [];
        for (const [key, { br, parts }] of groups) {
          out.push(
            <div
              key={`sh-wrap-${key}`}
              data-testid="shamrock-wrap"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: br.left,
                top: br.top,
                width: br.width,
                height: br.height,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 48,
              }}
            >
              {parts.map((p) => (
                <div
                  key={p.id}
                  data-testid="shamrock-particle"
                  style={{
                    position: 'absolute',
                    left: p.xFrac * br.width,
                    top: -p.size,
                    width: p.size,
                    height: p.size,
                    ['--fall-dist']: `${br.height + p.size}px`,
                    ['--sway-amp']: `${p.swayAmp}px`,
                    animation: `shamrockFall ${p.fallDur}ms cubic-bezier(.4,.05,.9,.6) ${p.delay}ms forwards, shamrockSway ${1200 + (p.fallDur % 800)}ms ease-in-out ${p.delay}ms infinite`,
                    willChange: 'transform',
                  }}
                >
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      ['--spin-deg']: `${p.rotSpin}deg`,
                      animation: `shamrockSpin ${p.fallDur}ms linear ${p.delay}ms forwards`,
                      filter: 'drop-shadow(0 0 6px #4cff5c) drop-shadow(0 0 14px rgba(80,255,120,0.55))',
                      willChange: 'transform',
                    }}
                  >
                    <img
                      src="/effects/shamrock.png"
                      alt=""
                      draggable={false}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        userSelect: 'none',
                        pointerEvents: 'none',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          );
        }
        return out;
      })()}

      {/* Water Gun strikes — for each strike, render an overflow:hidden
          wrapper sized to the clicked board, then place the water-gun
          image animating into the bottom-left of the board, plus a
          field of water-drop particles "soaking" the board, and a
          slow-fading wet patch overlay. */}
      {Array.isArray(waterStrikes) && waterStrikes.map((s) => {
        const br = s.boardRect;
        if (!br) return null;
        const gunW = Math.min(br.width, br.height) * 0.55;
        const gunH = gunW * (431 / 613);
        const gunLeft = 8;                       // small inset from left edge
        const gunTop  = (br.height - gunH) / 2;  // vertically centered

        // Big single-splash soak that fills most of the board, centered.
        const soakSize = Math.min(br.width, br.height) * 0.95;

        // Compute the vertical gap to the NEXT board below the soaked
        // one in canvas units, then convert to screen units. If there
        // is no board below, allow a generous default fall distance.
        // The drip layer is clipped to this gap height so the drops
        // visually disappear behind the next board's top edge.
        const allBoards = [
          ...(bingoBoards || []),
          ...(loteriaBoards || []),
        ];
        const sx = br.width  / (allBoards.find(b => Math.abs((b.x * (br.width  / (allBoards[0]?.width  || br.width))) ) >= 0)?.width  || 1);
        const _ignored = sx; // suppress unused-var (kept for future use)
        // The boardRect is already in screen pixels; convert by ratio.
        // Find soaked board's screen bounds: br. To find next board's
        // top screen coord we project canvas-y → screen-y using the
        // ratio between any board's screen height and canvas height.
        // Simpler: use canvas-units math directly with the matching
        // ratio derived from this strike.
        let gapPx = 80; // default if no board below
        if (allBoards.length > 0) {
          // Use the first board's screen size relative to its canvas
          // size as the universal y-scale (all boards share the same
          // scale).
          const refScreenW = br.width;
          const refCanvasW = allBoards.find(b => Math.abs(b.width - allBoards[0].width) < 1)?.width || br.width;
          const yScale = refScreenW / refCanvasW;
          // Soaked-board canvas-y top = br.top / yScale (approximate).
          const soakedTopCanvas = br.top / yScale;
          const soakedBottomCanvas = (br.top + br.height) / yScale;
          // Find boards whose top is BELOW the soaked board's bottom,
          // and horizontally overlap with it.
          const soakedLeftCanvas  = br.left / yScale;
          const soakedRightCanvas = (br.left + br.width) / yScale;
          let nearestNextTop = null;
          for (const b of allBoards) {
            if (b.y > soakedTopCanvas + 1) {
              const bLeft = b.x, bRight = b.x + b.width;
              if (bRight > soakedLeftCanvas && bLeft < soakedRightCanvas) {
                if (nearestNextTop == null || b.y < nearestNextTop) {
                  nearestNextTop = b.y;
                }
              }
            }
          }
          if (nearestNextTop != null) {
            const gapCanvas = nearestNextTop - soakedBottomCanvas;
            gapPx = Math.max(8, gapCanvas * yScale);
          }
        }

        // 7 teardrops spaced evenly along the soaked board's bottom
        // edge, each with a slight horizontal jitter and a randomized
        // size / delay / duration so they don't fall in lockstep.
        const dripCount = 7;
        const drips = [];
        let seed = s.id || 1;
        const rand = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
        for (let i = 0; i < dripCount; i++) {
          const xFrac = (i + 0.5) / dripCount + (rand() - 0.5) * 0.05;
          drips.push({
            id: `drip-${s.id}-${i}`,
            xFrac,
            size: 18 + rand() * 26,
            delay: 700 + rand() * 800,
            duration: 1100 + rand() * 700,
          });
        }

        // 4 side drops along each LEFT/RIGHT edge of the soaked board.
        // They start higher up on the board (around 30% from the top)
        // and trickle down, clipped by an edge layer with overflow
        // hidden so they fade out as they fall past the bottom corner.
        const sideDripCount = 4;
        const sideDripsLeft  = [];
        const sideDripsRight = [];
        for (let i = 0; i < sideDripCount; i++) {
          const yFrac = 0.30 + (i / sideDripCount) * 0.40 + (rand() - 0.5) * 0.04;
          const make = (side) => ({
            id: `side-${side}-${s.id}-${i}`,
            yFrac,
            size: 16 + rand() * 22,
            delay: 850 + rand() * 700,
            duration: 1200 + rand() * 700,
            side,
          });
          sideDripsLeft.push(make('l'));
          sideDripsRight.push(make('r'));
        }
        const sideFallPx = br.height * 0.55;
        return (
          <React.Fragment key={`wg-${s.id}`}>
            <div
              data-testid="watergun-wrap"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: br.left,
                top: br.top,
                width: br.width,
                height: br.height,
                pointerEvents: 'none',
                overflow: 'hidden',
                zIndex: 48,
              }}
            >
              {/* ONE big soak — the user's splash image rendered at ~95%
                  of the board, centred. Replaces the 36-droplet field. */}
              <img
                data-testid="watergun-soak"
                src="/effects/watersplash.png"
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: soakSize,
                  height: soakSize,
                  transform: 'translate(-50%, -50%) scale(0.2)',
                  opacity: 0,
                  objectFit: 'contain',
                  animation: 'waterBigSoak 2400ms ease-out forwards',
                  filter: 'drop-shadow(0 4px 8px rgba(40,120,200,0.45))',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
              {/* Continuous spray stream from the nozzle across the
                  board — kept for the "actively spraying" feel. */}
              <div
                style={{
                  position: 'absolute',
                  left: gunLeft + gunW * 0.85,
                  top:  gunTop + gunH * 0.40,
                  width: br.width,
                  height: gunH * 0.30,
                  background: 'linear-gradient(to right, rgba(180,225,255,0.92) 0%, rgba(140,210,255,0.65) 35%, rgba(120,200,255,0.30) 70%, rgba(120,200,255,0) 100%)',
                  borderRadius: '999px',
                  animation: 'waterStream 2000ms ease-out forwards',
                  filter: 'blur(2px)',
                  pointerEvents: 'none',
                }}
              />
              {/* Water gun — fully visible, slides in then holds. */}
              <img
                data-testid="watergun-img"
                src="/effects/watergun.png"
                alt=""
                draggable={false}
                style={{
                  position: 'absolute',
                  left: gunLeft,
                  top: gunTop,
                  width: gunW,
                  height: gunH,
                  animation: 'watergunSoak 2400ms ease-out forwards',
                  filter: 'drop-shadow(0 6px 8px rgba(0,0,0,0.35))',
                  pointerEvents: 'none',
                  userSelect: 'none',
                }}
              />
            </div>

            {/* Drip layer — overlaps the BOTTOM BORDER of the soaked
                board by `dropOverhang` px so the drops are visible
                hanging from the edge, AND extends down through the gap
                so they appear to fall behind the next board (clipped by
                overflow:hidden at the next board's top edge). Z-index
                49 so drips render on top of the soak. */}
            {(() => {
              const dropOverhang = 60;
              return (
                <div
                  data-testid="watergun-drip-layer"
                  aria-hidden="true"
                  style={{
                    position: 'absolute',
                    left: br.left,
                    top: br.top + br.height - dropOverhang,
                    width: br.width,
                    height: dropOverhang + gapPx,
                    pointerEvents: 'none',
                    overflow: 'hidden',
                    zIndex: 49,
                  }}
                >
                  {drips.map((d) => (
                    <img
                      key={d.id}
                      data-testid="watergun-drip"
                      src="/effects/waterdrop.png"
                      alt=""
                      draggable={false}
                      style={{
                        position: 'absolute',
                        left: `${d.xFrac * 100}%`,
                        top: 0,
                        width: d.size,
                        height: d.size * (673 / 478),
                        ['--drip-fall']: `${dropOverhang + gapPx + d.size}px`,
                        transform: 'translate(-50%, 0)',
                        animation: `watergunDripFall ${d.duration}ms ease-in ${d.delay}ms forwards`,
                        opacity: 0,
                        filter: 'drop-shadow(0 2px 3px rgba(40,120,200,0.55))',
                      }}
                    />
                  ))}
                </div>
              );
            })()}

            {/* Left & right edge runoff drips — start ~30% from the top
                of the soaked board and trickle down past the bottom
                corner. Each side is its own clipped layer so the drops
                disappear once they fall past the corner. */}
            {[
              { side: 'l', drips: sideDripsLeft,  edgeLeft: br.left - 12 },
              { side: 'r', drips: sideDripsRight, edgeLeft: br.left + br.width - 8 },
            ].map(({ side, drips: sd, edgeLeft }) => (
              <div
                key={`wg-side-${s.id}-${side}`}
                data-testid={`watergun-edge-${side}`}
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: edgeLeft,
                  top: br.top,
                  width: 28,
                  height: br.height + 12,
                  pointerEvents: 'none',
                  overflow: 'hidden',
                  zIndex: 49,
                }}
              >
                {sd.map((d) => (
                  <img
                    key={d.id}
                    data-testid="watergun-edge-drip"
                    src="/effects/waterdrop.png"
                    alt=""
                    draggable={false}
                    style={{
                      position: 'absolute',
                      left: '50%',
                      top: d.yFrac * br.height,
                      width: d.size,
                      height: d.size * (673 / 478),
                      ['--drip-fall']: `${sideFallPx}px`,
                      transform: 'translate(-50%, 0)',
                      animation: `watergunDripFall ${d.duration}ms ease-in ${d.delay}ms forwards`,
                      opacity: 0,
                      filter: 'drop-shadow(0 2px 3px rgba(40,120,200,0.55))',
                    }}
                  />
                ))}
              </div>
            ))}
          </React.Fragment>
        );
      })}
    </div>
  );
});

CanvasEditor.displayName = "CanvasEditor";
export default memo(CanvasEditor);
